import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Boxes,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Database,
  Filter,
  FlaskConical,
  GitBranch,
  Lock,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  Search,
  Settings,
  Shield,
  Sparkles,
  Table2,
} from 'lucide-react';
import { Alert, Badge, Button, Checkbox, Input, Spinner, Textarea } from '../../components/ui';
import { usePermissions } from '../../contexts/AuthContext';
import { cn } from '../../shared/lib/cn';
import {
  CatalogCorrection,
  CatalogAIEnrichmentRunResponse,
  CatalogLinkSummary,
  CatalogProviderSummary,
  CatalogColumnDetail,
  CatalogWarehouseAsset,
  CatalogWarehouseAssetPage,
  CatalogWarehouseAssetPageParams,
  CatalogWarehouseAssetDetail,
  useCatalogAIEnrichmentRun,
  useCatalogGlossary,
  useCatalogOverview,
  useCatalogProviders,
  useCatalogWarehouseAssetPage,
  useCatalogWarehouseAssetDetail,
  useCreateCatalogCorrection,
  useEnrichCatalogWarehouseAsset,
  useIndexTableValues,
  usePendingCatalogCorrections,
  useProfileConnectorSampleValues,
  useProfileTableSampleValues,
  useUpdateCatalogWarehouseColumn,
  useUpdateCatalogWarehouseTable,
} from '../../hooks/useCatalogApplet';
import {
  useProfileSamplesStream,
  useProfileConnectorSamplesStream,
} from '../../hooks/useProfileSamplesStream';
import { useIndexValuesStream } from '../../hooks/useIndexValuesStream';
import { AXIOS_INSTANCE } from '../../services/api-client';

type CatalogPageId = 'overview' | 'assets' | 'corrections' | 'providers' | 'settings';
type LinkStatus = CatalogWarehouseAsset['link_status'];
type SourceKind = 'atlan' | 'ai' | 'human' | 'warehouse' | 'missing' | 'eliza_native' | 'provider';
type CatalogAIEnrichmentMode = 'missing' | 'table' | 'selected_columns' | 'all_columns';
type CatalogAIEnrichmentPayload = {
  force?: boolean;
  mode?: CatalogAIEnrichmentMode;
  column_ids?: number[];
  batch_size?: number;
};
type LocalMetadataEditTarget =
  | {
      kind: 'table';
      id: number;
      title: string;
      description?: string | null;
    }
  | {
      kind: 'column';
      id: number;
      title: string;
      description?: string | null;
      alias?: string | null;
      aliases?: string[];
      isPii?: boolean;
      sampleValuesStatus?: string | null;
      semanticLookupEnabled?: boolean;
      semanticLookupEligible?: boolean;
      semanticLookupIneligibleReason?: string | null;
    };

const providerLabels: Record<string, string> = {
  eliza_native: 'Eliza-native',
  atlan: 'Atlan',
  dbt: 'dbt',
  openmetadata: 'OpenMetadata',
  unity: 'Unity Catalog',
  unity_catalog: 'Unity Catalog',
  yaml: 'YAML',
  bigquery: 'BigQuery',
};

const CATALOG_ASSET_PAGE_SIZE = 50;
const CATALOG_ASSET_SEARCH_MAX_LENGTH = 200;

const linkStatusCopy: Record<LinkStatus, { label: string; variant: 'success' | 'warning' | 'danger' | 'secondary' }> = {
  resolved: { label: 'Linked', variant: 'success' },
  missing: { label: 'Missing', variant: 'warning' },
  ambiguous: { label: 'Ambiguous', variant: 'danger' },
  catalog_unavailable: { label: 'Unavailable', variant: 'secondary' },
};

const availableProviders = [
  { key: 'atlan', name: 'Atlan', type: 'atlan', description: 'Enrich definitions, ownership, certification, classifications, and governed writeback.' },
  { key: 'dbt', name: 'dbt Cloud', type: 'dbt', description: 'Pull semantic models, exposures, lineage, and project artifacts into the catalog.' },
  { key: 'openmetadata', name: 'OpenMetadata', type: 'openmetadata', description: 'Open-source metadata, ownership, lineage, and data quality enrichment.' },
  { key: 'unity', name: 'Unity Catalog', type: 'unity_catalog', description: 'Databricks Unity Catalog governance, discovery, and lineage metadata.' },
  { key: 'yaml', name: 'YAML adapter', type: 'yaml', description: 'Hand-authored catalog metadata from committed YAML files.' },
];

function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '0';
  return new Intl.NumberFormat().format(value);
}

function formatRows(value: number | null | undefined): string {
  if (!value) return '--';
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(value >= 10_000_000 ? 0 : 1)}M`;
  if (value >= 1_000) return `${Math.round(value / 1_000)}K`;
  return formatNumber(value);
}

function formatDate(value?: string | null): string {
  if (!value) return 'Not synced';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not synced';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function providerLabel(providerType?: string | null): string {
  if (!providerType) return 'Eliza-native';
  return providerLabels[providerType] || providerType;
}

function pageFromPath(pathname: string): CatalogPageId {
  if (pathname.startsWith('/catalog/assets')) return 'assets';
  if (pathname.startsWith('/catalog/corrections')) return 'corrections';
  if (pathname.startsWith('/catalog/providers')) return 'providers';
  if (pathname.startsWith('/catalog/settings')) return 'settings';
  return 'overview';
}

function positiveNumberParam(value: string | null): number | undefined {
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

function nonNegativeNumberParam(value: string | null): number | undefined {
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
}

function linkStatusParam(value: string | null): CatalogWarehouseAsset['link_status'] | undefined {
  if (
    value === 'resolved' ||
    value === 'missing' ||
    value === 'ambiguous' ||
    value === 'catalog_unavailable'
  ) {
    return value;
  }
  return undefined;
}

function catalogSearchParam(value: string | null | undefined): string | undefined {
  const search = value?.trim().slice(0, CATALOG_ASSET_SEARCH_MAX_LENGTH);
  return search || undefined;
}

function catalogAssetPageParams(searchParams: URLSearchParams): CatalogWarehouseAssetPageParams {
  const search = catalogSearchParam(searchParams.get('search'));
  const hasPii = searchParams.get('has_pii');
  const warehouseConnectorId = positiveNumberParam(searchParams.get('warehouse_connector_id'));
  const linkStatus = linkStatusParam(searchParams.get('link_status'));
  return {
    limit: positiveNumberParam(searchParams.get('limit')) ?? CATALOG_ASSET_PAGE_SIZE,
    offset: nonNegativeNumberParam(searchParams.get('offset')) ?? 0,
    ...(search ? { search } : {}),
    ...(warehouseConnectorId ? { warehouse_connector_id: warehouseConnectorId } : {}),
    ...(linkStatus ? { link_status: linkStatus } : {}),
    ...(hasPii === 'true' ? { has_pii: true } : {}),
  };
}

function assetDisplayName(asset: CatalogWarehouseAsset | CatalogWarehouseAssetDetail): string {
  return asset.name || asset.table_external_id || asset.qualified_name.split('.').pop() || asset.qualified_name;
}

function datasetDisplay(asset: CatalogWarehouseAsset | CatalogWarehouseAssetDetail): string {
  return [asset.project, asset.dataset].filter(Boolean).join('.') || asset.dataset || asset.project || asset.warehouse_connector_name;
}

function sourceForAsset(asset: CatalogWarehouseAsset | CatalogWarehouseAssetDetail): { source: SourceKind; label: string; confidence?: number } {
  const link = asset.catalog_links[0];
  if (link) {
    const provider = link.catalog_asset.provider_type;
    return {
      source: provider === 'atlan' ? 'atlan' : provider === 'eliza_native' ? 'eliza_native' : 'provider',
      label: providerLabel(provider),
      confidence: link.confidence_score,
    };
  }
  if (asset.link_status === 'resolved') return { source: 'eliza_native', label: 'Eliza-native' };
  return { source: 'warehouse', label: providerLabel(asset.warehouse_type) };
}

function hasDescription(asset: Pick<CatalogWarehouseAsset, 'effective_description' | 'source_description' | 'local_override_description'>): boolean {
  return Boolean(asset.effective_description || asset.local_override_description || asset.source_description);
}

function catalogEnrichmentRunSummary(
  run: CatalogAIEnrichmentRunResponse
): { message: string; failed: boolean; shouldRefetch: boolean } | null {
  if (run.status === 'failed') {
    return {
      message: run.error_message || 'AI enrichment failed. Confirm the backend has an OpenAI key configured, then try again.',
      failed: true,
      shouldRefetch: false,
    };
  }
  if (run.status !== 'completed') {
    return null;
  }

  const result = run.result;
  const mode = result?.mode || run.mode;
  const modeLabel = catalogEnrichmentModeLabel(mode);
  if (!result) {
    return {
      message: `${modeLabel} completed. Refreshing catalog metadata.`,
      failed: false,
      shouldRefetch: true,
    };
  }
  if (result.status === 'skipped') {
    return {
      message: result.skipped_reason || 'Metadata already exists for this table.',
      failed: false,
      shouldRefetch: false,
    };
  }

  const updatedColumns = result.columns.filter((column) => column.updated).length;
  const batchCopy = result.batch_size ? ` Column batches used up to ${result.batch_size} fields.` : '';
  return {
    message: `${modeLabel} updated ${result.table_updated ? 'the table' : '0 table fields'} and ${updatedColumns} column${updatedColumns === 1 ? '' : 's'}.${batchCopy}`,
    failed: false,
    shouldRefetch: true,
  };
}

function catalogEnrichmentModeLabel(mode?: CatalogAIEnrichmentMode | null): string {
  if (mode === 'table') return 'Table description redraft';
  if (mode === 'selected_columns') return 'Selected column redraft';
  if (mode === 'all_columns') return 'All-column redraft';
  return 'AI metadata draft';
}

function assetStatusBadge(asset: CatalogWarehouseAsset | CatalogWarehouseAssetDetail) {
  const copy = asset.link_status === 'missing' && hasDescription(asset)
    ? { label: 'Described', variant: 'success' as const }
    : asset.link_status === 'missing'
      ? { label: 'Needs metadata', variant: 'warning' as const }
      : linkStatusCopy[asset.link_status];
  return <Badge variant={copy.variant}>{copy.label}</Badge>;
}

function CatalogSurface({
  children,
  className,
  padded = true,
}: {
  children: React.ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return (
    <section className={cn('rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-dark-border dark:bg-dark-surface', padded && 'p-5', className)}>
      {children}
    </section>
  );
}

function SectionHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4 dark:border-dark-border">
      <div>
        <h3 className="font-subtitle text-lg text-charcoal dark:text-gray-100">{title}</h3>
        {description && <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{description}</p>}
      </div>
      {action}
    </div>
  );
}

function ActionLink({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-eliza-red hover:text-eliza-red-light"
    >
      {children}
      <ArrowRight className="h-3.5 w-3.5" />
    </button>
  );
}

function FilterChip({
  active,
  children,
  onClick,
}: {
  active?: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors',
        active
          ? 'border-charcoal bg-charcoal text-white dark:border-gray-100 dark:bg-gray-100 dark:text-charcoal'
          : 'border-gray-200 bg-white text-charcoal hover:border-gray-300 hover:bg-gray-50 dark:border-dark-border dark:bg-dark-surface dark:text-gray-200 dark:hover:bg-dark-surface-2'
      )}
    >
      {children}
    </button>
  );
}

function SourcePill({ source, label, confidence }: { source: SourceKind; label: string; confidence?: number }) {
  const classes: Record<SourceKind, string> = {
    atlan: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/30 dark:text-violet-300 dark:border-violet-900',
    ai: 'bg-eliza-red/10 text-eliza-red border-eliza-red/20',
    human: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-900',
    warehouse: 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-dark-surface-2 dark:text-gray-300 dark:border-dark-border',
    missing: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/30 dark:text-rose-300 dark:border-rose-900',
    eliza_native: 'bg-eliza-red/10 text-eliza-red border-eliza-red/20',
    provider: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/30 dark:text-sky-300 dark:border-sky-900',
  };
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium', classes[source])}>
      {source === 'ai' || source === 'eliza_native' ? <Sparkles className="h-3 w-3" /> : <span className="h-1.5 w-1.5 rounded-full bg-current" />}
      {label}{confidence ? ` · ${Math.round(confidence * 100)}%` : ''}
    </span>
  );
}

function ApiState({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 403) {
    return (
      <CatalogSurface className="py-12 text-center">
        <Shield className="mx-auto mb-4 h-10 w-10 text-gray-400" />
        <h2 className="font-subtitle text-xl text-charcoal dark:text-gray-100">Catalog access required</h2>
        <p className="mx-auto mt-2 max-w-lg text-sm text-gray-500 dark:text-gray-400">
          This applet is available to tenant admins and catalog stewards with catalog or connector read access.
        </p>
      </CatalogSurface>
    );
  }

  return (
    <Alert variant="error" title="Catalog data could not be loaded">
      <div className="mt-3 flex items-center justify-between gap-4">
        <span>Retry the request or check whether the catalog registry routes are enabled for this tenant.</span>
        <Button variant="outline" size="sm" onClick={onRetry}>Retry</Button>
      </div>
    </Alert>
  );
}

function AppletHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow: string;
  title: string;
  description: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className="mb-6 flex items-start justify-between gap-5">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-gray-500">{eyebrow}</div>
        <h1 className="mt-1 font-title text-3xl leading-tight text-charcoal dark:text-gray-100">{title}</h1>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-500 dark:text-gray-400">{description}</p>
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

function StatCard({ label, value, subtext }: { label: string; value: string; subtext: React.ReactNode }) {
  return (
    <CatalogSurface className="min-h-[112px]" padded>
      <div className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-gray-500">{label}</div>
      <div className="mt-2 font-subtitle text-[28px] leading-none text-charcoal dark:text-gray-100">{value}</div>
      <div className="mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400">{subtext}</div>
    </CatalogSurface>
  );
}

function coverageRows(assets: CatalogWarehouseAsset[]) {
  const map = new Map<string, CatalogWarehouseAsset[]>();
  assets.forEach((asset) => {
    const key = asset.warehouse_connector_name || asset.project || 'Warehouse';
    map.set(key, [...(map.get(key) || []), asset]);
  });
  return Array.from(map.entries()).map(([name, group]) => {
    const linked = group.filter((asset) => asset.link_status === 'resolved').length;
    const described = group.filter((asset) => hasDescription(asset)).length;
    const missing = group.length - described;
    return {
      name,
      total: group.length,
      linked,
      described,
      missing,
      pct: group.length ? Math.round((described / group.length) * 100) : 0,
      lastSync: group.map((asset) => asset.last_synced_at).filter(Boolean).sort().pop(),
    };
  });
}

function CatalogOverviewSurface({
  overview,
  assets,
  corrections,
  providers,
  onNavigate,
  onSync,
  canViewCorrectionQueue,
}: {
  overview?: ReturnType<typeof useCatalogOverview>['data'];
  assets: CatalogWarehouseAsset[];
  corrections: CatalogCorrection[];
  providers: CatalogProviderSummary[];
  onNavigate: (page: CatalogPageId) => void;
  onSync: () => void;
  canViewCorrectionQueue: boolean;
}) {
  const rows = coverageRows(assets);
  const activity = buildActivity(overview, providers, corrections, assets);
  const hasAssetRows = assets.length > 0;
  const metadataCoverage = hasAssetRows
    ? Math.round((assets.filter((asset) => hasDescription(asset)).length / assets.length) * 100)
    : Math.round(overview?.coverage_percentage || 0);
  const describedAssetCount = hasAssetRows
    ? assets.filter((asset) => hasDescription(asset)).length
    : Math.round(((overview?.coverage_percentage || 0) / 100) * (overview?.total_warehouse_assets || 0));
  const providerLinkedCount = hasAssetRows
    ? assets.filter((asset) => asset.link_status === 'resolved').length
    : overview?.linked_warehouse_assets || 0;
  const columnCount = hasAssetRows
    ? assets.reduce((sum, asset) => sum + asset.column_count, 0)
    : null;

  return (
    <>
      <AppletHeader
        eyebrow="Catalog · Tenant"
        title={`${formatNumber(overview?.total_warehouse_assets)} assets indexed`}
        description="Eliza-native catalog backbone, enriched by connected providers. Workspaces consume what is certified; corrections land here."
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={onSync}><RefreshCw className="h-4 w-4" />Sync now</Button>
            <Button variant="secondary" size="sm" onClick={() => onNavigate('providers')}><Filter className="h-4 w-4" />Configure providers</Button>
          </>
        }
      />

      {overview && !overview.registry_available && (
        <Alert className="mb-6" variant="warning" title="Catalog registry tables are unavailable">
          Warehouse assets can still be inspected when present, but provider links, glossary terms, and correction queues require the catalog registry migration.
        </Alert>
      )}

      <div className="mb-7 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Assets"
          value={formatNumber(overview?.total_warehouse_assets)}
          subtext={`${formatNumber(describedAssetCount)} described · ${formatNumber(providerLinkedCount)} provider-linked${columnCount === null ? '' : ` · ${formatNumber(columnCount)} columns`}`}
        />
        <StatCard
          label="Metadata coverage"
          value={`${metadataCoverage}%`}
          subtext={<span className="inline-flex items-center gap-1"><Sparkles className="h-3 w-3 text-eliza-red" /> Tables with warehouse, local, AI, or provider descriptions</span>}
        />
        <StatCard
          label="Pending review"
          value={canViewCorrectionQueue ? formatNumber(overview?.pending_corrections || corrections.length) : '--'}
          subtext={
            canViewCorrectionQueue
              ? `${formatNumber(corrections.length)} correction${corrections.length === 1 ? '' : 's'} visible to this user`
              : 'Correction queue access requires catalog steward permissions'
          }
        />
        <StatCard
          label="Workspaces using"
          value="--"
          subtext={`${hasAssetRows ? `${formatNumber(rows.length)} warehouses · ` : ''}${formatNumber(overview?.providers?.length || providers.length)} provider${(overview?.providers?.length || providers.length) === 1 ? '' : 's'}`}
        />
      </div>

      <div className="grid gap-5 xl:grid-cols-[1.4fr_1fr]">
        <CatalogSurface padded={false}>
          <SectionHeader
            title="Coverage by warehouse"
            description="How much of each warehouse has usable table descriptions from warehouse sync, AI drafts, local edits, or providers."
            action={<ActionLink onClick={() => onNavigate('assets')}>Open assets</ActionLink>}
          />
          <div className="px-5 py-4">
            <div className="mb-3 flex flex-wrap gap-4 text-xs text-gray-500 dark:text-gray-400">
              <span className="inline-flex items-center gap-1.5"><i className="h-2 w-2 rounded-sm bg-violet-600" /> Provider-linked</span>
              <span className="inline-flex items-center gap-1.5"><i className="h-2 w-2 rounded-sm bg-eliza-red" /> Described locally or by AI</span>
              <span className="inline-flex items-center gap-1.5"><i className="h-2 w-2 rounded-sm border border-dashed border-gray-300" /> Missing</span>
            </div>
            {rows.length ? rows.map((row) => (
              <div key={row.name} className="grid grid-cols-[1.25fr_1.4fr_auto] items-center gap-4 border-b border-gray-100 py-3 last:border-b-0 dark:border-dark-border">
                <div>
                  <div className="text-sm font-medium text-charcoal dark:text-gray-100">{row.name}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{formatNumber(row.total)} assets · synced {formatDate(row.lastSync)}</div>
                </div>
                <div className="flex h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-dark-surface-2">
                  <i className="bg-violet-600" style={{ width: `${row.total ? Math.round((row.linked / row.total) * 100) : 0}%` }} />
                  <i className="bg-eliza-red/70" style={{ width: `${row.total ? Math.max(0, row.pct - Math.round((row.linked / row.total) * 100)) : 0}%` }} />
                </div>
                <div className="whitespace-nowrap text-xs tabular-nums text-gray-500 dark:text-gray-400">{row.pct}% described</div>
              </div>
            )) : (
              <EmptyState title="No warehouses indexed" description="Warehouse assets will appear here after the registry syncs." />
            )}
          </div>
        </CatalogSurface>

        <CatalogSurface padded={false}>
          <SectionHeader title="Recent activity" description="Catalog changes across available sources." action={<ActionLink>All activity</ActionLink>} />
          <div className="px-5 py-3">
            {activity.map((item) => (
              <div key={item.id} className="flex gap-3 border-b border-gray-100 py-3 last:border-b-0 dark:border-dark-border">
                <span className={cn('inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full', item.tone)}>
                  <item.icon className="h-3.5 w-3.5" />
                </span>
                <div className="text-sm leading-5 text-charcoal dark:text-gray-100">
                  {item.text}
                  <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{item.meta}</div>
                </div>
              </div>
            ))}
          </div>
        </CatalogSurface>
      </div>

      {canViewCorrectionQueue && (
        <div className="mt-5">
          <CatalogSurface padded={false}>
            <SectionHeader
              title="Pending corrections"
              description="Workspaces hand off corrections here for steward review and governed writeback."
              action={<ActionLink onClick={() => onNavigate('corrections')}>Open queue</ActionLink>}
            />
            <div className="px-5 pb-4">
              {corrections.length ? corrections.slice(0, 3).map((correction) => (
                <div key={correction.id} className="grid grid-cols-[20px_1.4fr_1fr_0.6fr_auto] items-center gap-4 border-b border-gray-100 py-3 last:border-b-0 dark:border-dark-border">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <div>
                    <div className="font-mono text-xs text-charcoal dark:text-gray-100">{correction.catalog_asset?.display_name || correction.catalog_asset?.name || correction.field_path}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Field <code className="rounded bg-gray-100 px-1 py-0.5 font-mono dark:bg-dark-surface-2">{correction.field_path}</code></div>
                  </div>
                  <div className="truncate text-sm text-charcoal dark:text-gray-100">{correction.proposed_value}</div>
                  <Badge variant={correction.catalog_asset?.provider_type === 'atlan' ? 'info' : 'secondary'}>
                    {correction.catalog_asset?.provider_type === 'atlan' ? 'Writeback to Atlan' : 'Eliza-native'}
                  </Badge>
                  <ActionLink onClick={() => onNavigate('corrections')}>Review</ActionLink>
                </div>
              )) : (
                <EmptyState title="No pending corrections" description="Corrections raised from workspace workflows will appear here." />
              )}
            </div>
          </CatalogSurface>
        </div>
      )}
    </>
  );
}

function buildActivity(
  overview: ReturnType<typeof useCatalogOverview>['data'] | undefined,
  providers: CatalogProviderSummary[],
  corrections: CatalogCorrection[],
  assets: CatalogWarehouseAsset[]
) {
  const items = [];
  if (overview?.registry_available === false) {
    items.push({
      id: 'registry-unavailable',
      icon: AlertTriangle,
      tone: 'bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300',
      text: 'Catalog registry tables are unavailable for this deployment.',
      meta: 'Migration required',
    });
  }
  providers.filter((provider) => provider.last_synced_at).slice(0, 2).forEach((provider) => {
    items.push({
      id: `provider-${provider.provider_type}-${provider.connector_id || provider.connector_name}`,
      icon: RefreshCw,
      tone: 'bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300',
      text: `${provider.connector_name} catalog sync metadata is available.`,
      meta: `Synced ${formatDate(provider.last_synced_at)} · ${providerLabel(provider.provider_type)}`,
    });
  });
  corrections.slice(0, 2).forEach((correction) => {
    items.push({
      id: `correction-${correction.id}`,
      icon: Sparkles,
      tone: 'bg-eliza-red/10 text-eliza-red',
      text: `Correction requested for ${correction.field_path}.`,
      meta: `${formatDate(correction.created_at)} · ${correction.status}`,
    });
  });
  const drift = assets.find((asset) => asset.has_drift);
  if (drift) {
    items.push({
      id: `drift-${drift.id}`,
      icon: Database,
      tone: 'bg-violet-50 text-violet-700 dark:bg-violet-950/30 dark:text-violet-300',
      text: `Warehouse drift detected for ${drift.qualified_name}.`,
      meta: 'Needs resolver review',
    });
  }
  if (!items.length) {
    items.push({
      id: 'quiet',
      icon: CheckCircle2,
      tone: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300',
      text: 'No recent catalog changes visible to this user.',
      meta: 'Catalog activity will appear after syncs, corrections, or provider updates.',
    });
  }
  return items.slice(0, 5);
}

function EmptyState({ title, description }: { title: string; description: string }) {
  return (
    <div className="py-10 text-center">
      <Boxes className="mx-auto mb-3 h-9 w-9 text-gray-300 dark:text-gray-600" />
      <p className="font-medium text-charcoal dark:text-gray-100">{title}</p>
      <p className="mx-auto mt-1 max-w-md text-sm text-gray-500 dark:text-gray-400">{description}</p>
    </div>
  );
}

function CatalogAssetsSurface({
  assetPage,
  query,
  isFetching,
  onQueryChange,
}: {
  assetPage?: CatalogWarehouseAssetPage;
  query: CatalogWarehouseAssetPageParams;
  isFetching?: boolean;
  onQueryChange: (patch: Partial<CatalogWarehouseAssetPageParams>, options?: { resetOffset?: boolean }) => void;
}) {
  const navigate = useNavigate();
  const profileConnector = useProfileConnectorSampleValues();
  const assets = assetPage?.items || [];
  const totalAssets = assetPage?.total || 0;
  const pageLimit = assetPage?.limit || query.limit;
  const pageOffset = assetPage?.offset || query.offset;
  const pageStart = totalAssets ? Math.min(pageOffset + 1, totalAssets) : 0;
  const pageEnd = Math.min(pageOffset + assets.length, totalAssets);
  const canPageBack = pageOffset > 0;
  const canPageForward = pageOffset + pageLimit < totalAssets;
  const [bulkMessage, setBulkMessage] = useState<string | null>(null);
  const [bulkTaskId, setBulkTaskId] = useState<string | null>(null);
  const [bulkNeedsReconnect, setBulkNeedsReconnect] = useState(false);
  const [bulkReconnecting, setBulkReconnecting] = useState(false);
  const [bulkReconnectConnectorId, setBulkReconnectConnectorId] = useState<number | null>(null);
  const [localSearch, setLocalSearch] = useState(query.search || '');
  const warehouses = assetPage?.facets.warehouses || [];
  const activeSourceFilter: 'all' | LinkStatus | 'pii' = query.has_pii
    ? 'pii'
    : query.link_status || 'all';

  useEffect(() => {
    setLocalSearch(query.search || '');
  }, [query.search]);

  useEffect(() => {
    const nextSearch = catalogSearchParam(localSearch);
    if (nextSearch === (query.search || '')) return;
    const timer = window.setTimeout(() => {
      onQueryChange({ search: nextSearch }, { resetOffset: true });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [localSearch, onQueryChange, query.search]);

  // Resolve a single warehouse connector to bulk-profile from result facets,
  // not just the current page of rows.
  const bulkConnectorId = query.warehouse_connector_id ?? (warehouses.length === 1 ? warehouses[0].id : null);

  const bulkStream = useProfileConnectorSamplesStream({
    connectorId: bulkConnectorId,
    taskId: bulkTaskId,
    onComplete: (event) => {
      const result = (event.result || {}) as {
        error?: string;
        message?: string;
        tables_succeeded?: number;
        columns_profiled?: number;
      };
      if (result.error === 'auth_required') {
        setBulkNeedsReconnect(true);
        setBulkReconnectConnectorId(bulkConnectorId);
        setBulkMessage(
          result.message || 'Reconnect your Google BigQuery account to profile sample values.'
        );
        setBulkTaskId(null);
        return;
      }
      const tables = result.tables_succeeded ?? 0;
      const cols = result.columns_profiled ?? 0;
      setBulkMessage(
        `Bulk profiling complete: ${tables} table${tables === 1 ? '' : 's'}, ` +
          `${cols} column${cols === 1 ? '' : 's'} now have live sample values.`
      );
      setBulkTaskId(null);
    },
    onError: (event) => {
      setBulkMessage(
        event.event_type === 'timeout'
          ? 'Bulk profiling is taking longer than expected. It may still finish in the background.'
          : event.error || 'Bulk profiling failed.'
      );
      setBulkTaskId(null);
    },
  });

  const isBulkProfiling = profileConnector.isPending || bulkStream.isRunning;

  const runBulkProfiling = async () => {
    if (bulkConnectorId == null) return;
    setBulkMessage(null);
    setBulkNeedsReconnect(false);
    setBulkTaskId(null);
    try {
      const result = await profileConnector.mutateAsync({
        connectorId: bulkConnectorId,
        force: true,
        categorical: true,
      });
      if (result.queued && result.task_id) {
        setBulkTaskId(result.task_id);
        setBulkMessage(
          'Bulk sample value profiling running for this warehouse. Values appear on each table as the run completes.'
        );
        return;
      }
      setBulkMessage(
        result.queued
          ? 'Bulk sample value profiling queued for this warehouse. Values appear on each table once the run completes.'
          : result.error || 'Bulk profiling could not be queued.'
      );
    } catch {
      // The mutation state drives the visible error alert.
    }
  };

  const handleBulkReconnectBigQuery = async () => {
    const connectorId = bulkReconnectConnectorId ?? bulkConnectorId;
    if (connectorId == null || bulkReconnecting) return;
    setBulkReconnecting(true);
    try {
      const { data } = await AXIOS_INSTANCE.get('/oauth/authorize/bigquery', {
        params: {
          connector_config_id: connectorId,
          return_origin: window.location.origin,
        },
      });
      const popup = window.open(data.authorize_url, 'bigquery_oauth', 'width=640,height=760');
      if (!popup) {
        setBulkMessage('Popup blocked. Allow popups for this site, then try reconnecting.');
        setBulkReconnecting(false);
        return;
      }

      const cleanup = () => {
        window.removeEventListener('message', handler);
        clearInterval(interval);
        setBulkReconnecting(false);
      };

      const handler = (event: MessageEvent) => {
        if (event.data?.type === 'oauth_complete' && event.data?.source === 'bigquery') {
          cleanup();
          setBulkNeedsReconnect(false);
          setBulkMessage('BigQuery reconnected. Re-running bulk profiling…');
          runBulkProfiling();
        }
      };

      window.addEventListener('message', handler);
      const interval = window.setInterval(() => {
        if (popup.closed) cleanup();
      }, 1000);
    } catch (error: any) {
      setBulkReconnecting(false);
      setBulkMessage(
        error?.response?.data?.detail || error?.message || 'Failed to start BigQuery reconnect.'
      );
    }
  };
  return (
    <>
      <AppletHeader
        eyebrow="Catalog · Assets"
        title="Asset browser"
        description="Every table and view across all warehouses, enriched by Eliza-native and connected providers. Click any asset to view columns and metadata."
        actions={
          <>
            <Button
              variant="secondary"
              size="sm"
              disabled={bulkConnectorId == null || isBulkProfiling}
              title={
                bulkConnectorId == null
                  ? 'Select a single warehouse to profile all of its tables.'
                  : 'Profile DB sample values for every table in this warehouse.'
              }
              onClick={runBulkProfiling}
            >
              {isBulkProfiling ? <Spinner className="h-4 w-4" /> : <FlaskConical className="h-4 w-4" />}
              {bulkStream.isRunning ? 'Profiling…' : 'Profile all tables'}
            </Button>
            <Button variant="secondary" size="sm"><Filter className="h-4 w-4" />Saved views</Button>
            <Button variant="secondary" size="sm"><Plus className="h-4 w-4" />Add manual entry</Button>
          </>
        }
      />

      {(bulkMessage || profileConnector.isError) && (
        <Alert
          className="mb-4"
          variant={profileConnector.isError ? 'error' : bulkNeedsReconnect ? 'warning' : 'info'}
          title={
            profileConnector.isError
              ? 'Bulk profiling failed'
              : bulkNeedsReconnect
                ? 'Reconnect Google BigQuery'
                : 'Sample value profiling'
          }
        >
          <div className="flex flex-col gap-2">
            <span>
              {profileConnector.isError
                ? 'Bulk profiling requires a background worker. Confirm Celery is running, then try again.'
                : bulkMessage}
            </span>
            {bulkNeedsReconnect && (
              <div>
                <Button
                  variant="brand"
                  size="sm"
                  disabled={bulkReconnecting}
                  onClick={handleBulkReconnectBigQuery}
                >
                  {bulkReconnecting ? <Spinner className="h-4 w-4" /> : <Database className="h-4 w-4" />}
                  Reconnect Google BigQuery
                </Button>
              </div>
            )}
          </div>
        </Alert>
      )}

      <CatalogSurface padded={false}>
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-dark-border dark:bg-dark-surface-2">
          <div className="flex h-9 min-w-[280px] flex-1 max-w-[360px] items-center gap-2 rounded-full border border-gray-200 bg-white px-3 transition-colors focus-within:border-eliza-red/30 focus-within:ring-2 focus-within:ring-eliza-red/15 dark:border-dark-border dark:bg-dark-surface dark:focus-within:border-eliza-red-light/30 dark:focus-within:ring-eliza-red-light/15">
            <Search className="h-4 w-4 text-gray-400" />
            <input
              aria-label="Filter catalog assets"
              value={localSearch}
              onChange={(event) => setLocalSearch(event.target.value.slice(0, CATALOG_ASSET_SEARCH_MAX_LENGTH))}
              maxLength={CATALOG_ASSET_SEARCH_MAX_LENGTH}
              placeholder={`Filter ${formatNumber(totalAssets)} assets by name, dataset, domain...`}
              className="min-w-0 flex-1 border-0 bg-transparent text-sm outline-none ring-0 placeholder:text-gray-400 focus:border-0 focus:outline-none focus:ring-0 dark:text-gray-100"
            />
          </div>
          <span className="h-6 w-px bg-gray-200 dark:bg-dark-border" />
          <FilterChip
            active={!query.warehouse_connector_id}
            onClick={() => onQueryChange({ warehouse_connector_id: undefined }, { resetOffset: true })}
          >
            All warehouses
          </FilterChip>
          {warehouses.map((warehouse) => (
            <FilterChip
              key={warehouse.id}
              active={query.warehouse_connector_id === warehouse.id}
              onClick={() => onQueryChange({ warehouse_connector_id: warehouse.id }, { resetOffset: true })}
            >
              {warehouse.name.replace('BigQuery - ', '').replace('BigQuery — ', '')}
              <span className="text-[10px] opacity-70">{formatNumber(warehouse.asset_count)}</span>
            </FilterChip>
          ))}
          <span className="h-6 w-px bg-gray-200 dark:bg-dark-border" />
          <FilterChip
            active={activeSourceFilter === 'resolved'}
            onClick={() => onQueryChange(
              activeSourceFilter === 'resolved' ? { link_status: undefined } : { link_status: 'resolved', has_pii: undefined },
              { resetOffset: true }
            )}
          >
            <BookOpen className="h-3 w-3" /> Linked
          </FilterChip>
          <FilterChip
            active={activeSourceFilter === 'missing'}
            onClick={() => onQueryChange(
              activeSourceFilter === 'missing' ? { link_status: undefined } : { link_status: 'missing', has_pii: undefined },
              { resetOffset: true }
            )}
          >
            <AlertTriangle className="h-3 w-3" /> Needs metadata
          </FilterChip>
          <FilterChip
            active={activeSourceFilter === 'pii'}
            onClick={() => onQueryChange(
              activeSourceFilter === 'pii' ? { has_pii: undefined } : { has_pii: true, link_status: undefined },
              { resetOffset: true }
            )}
          >
            <Lock className="h-3 w-3" /> PII
            {assetPage?.facets.pii_asset_count ? <span className="text-[10px] opacity-70">{formatNumber(assetPage.facets.pii_asset_count)}</span> : null}
          </FilterChip>
          {isFetching && <Spinner className="ml-auto h-4 w-4" />}
        </div>

        {assets.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse">
              <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-dark-surface-2">
                <tr className="border-b border-gray-200 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-400 dark:border-dark-border dark:text-gray-500">
                  <th className="px-4 py-3">Asset</th>
                  <th className="px-4 py-3">Warehouse</th>
                  <th className="px-4 py-3">Catalog enrichment</th>
                  <th className="px-4 py-3">Description</th>
                  <th className="px-4 py-3">Workspaces</th>
                  <th className="px-4 py-3">Rows · Fresh</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {assets.map((asset) => {
                  const source = sourceForAsset(asset);
                  return (
                    <tr
                      key={asset.id}
                      onClick={() => navigate(`/catalog/assets/${asset.id}`)}
                      className="cursor-pointer border-b border-gray-100 transition-colors hover:bg-gray-50 dark:border-dark-border dark:hover:bg-dark-surface-2"
                    >
                      <td className="min-w-[260px] px-4 py-3">
                        <div className="flex items-center gap-2">
                          <AssetTypeIcon asset={asset} />
                          <b className="text-sm font-medium text-charcoal dark:text-gray-100">{assetDisplayName(asset)}</b>
                          {assetStatusBadge(asset)}
                          {asset.pii_column_count > 0 && <Badge variant="warning">PII</Badge>}
                        </div>
                        <div className="mt-1 font-mono text-xs text-gray-500 dark:text-gray-400">{asset.qualified_name} · {asset.column_count} cols</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-charcoal dark:text-gray-100">
                        {asset.warehouse_connector_name}
                        <div className="text-xs text-gray-500 dark:text-gray-400">{datasetDisplay(asset)}</div>
                      </td>
                      <td className="px-4 py-3"><SourcePill {...source} /></td>
                      <td className={cn('max-w-[360px] px-4 py-3 text-sm leading-5', asset.effective_description ? 'text-charcoal dark:text-gray-100' : 'italic text-gray-400 dark:text-gray-500')}>
                        {asset.effective_description || asset.link_status_reason || 'No description yet. Open the table to draft AI metadata.'}
                      </td>
                      <td className="px-4 py-3"><span className="rounded-full border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-500 dark:border-dark-border dark:bg-dark-surface-2">--</span></td>
                      <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-gray-500 dark:text-gray-400">
                        <div>{formatRows(asset.row_count_snapshot)}</div>
                        <div>{formatDate(asset.last_synced_at)}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-400"><ChevronRight className="h-4 w-4" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="flex justify-between border-t border-gray-200 px-5 py-3 text-xs text-gray-500 dark:border-dark-border dark:text-gray-400">
              <span>Showing {formatNumber(pageStart)}-{formatNumber(pageEnd)} of {formatNumber(totalAssets)}</span>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={!canPageBack}
                  onClick={() => onQueryChange({ offset: Math.max(pageOffset - pageLimit, 0) })}
                >
                  <ArrowLeft className="h-4 w-4" /> Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={!canPageForward}
                  onClick={() => onQueryChange({ offset: pageOffset + pageLimit })}
                >
                  Next <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
              <span className="inline-flex items-center gap-1"><Sparkles className="h-3 w-3 text-eliza-red" /> AI drafts missing metadata from a table detail page.</span>
            </div>
          </div>
        ) : (
          <>
            <EmptyState title="No catalog assets found" description={totalAssets ? 'This page is empty. Go to the previous page or adjust the filters.' : 'No warehouse registry assets are available for this tenant yet.'} />
            {totalAssets > 0 && (
              <div className="flex justify-between border-t border-gray-200 px-5 py-3 text-xs text-gray-500 dark:border-dark-border dark:text-gray-400">
                <span>Showing {formatNumber(pageStart)}-{formatNumber(pageEnd)} of {formatNumber(totalAssets)}</span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!canPageBack}
                    onClick={() => onQueryChange({ offset: Math.max(pageOffset - pageLimit, 0) })}
                  >
                    <ArrowLeft className="h-4 w-4" /> Previous
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!canPageForward}
                    onClick={() => onQueryChange({ offset: pageOffset + pageLimit })}
                  >
                    Next <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
                <span className="inline-flex items-center gap-1"><Sparkles className="h-3 w-3 text-eliza-red" /> AI drafts missing metadata from a table detail page.</span>
              </div>
            )}
          </>
        )}
      </CatalogSurface>
    </>
  );
}

function AssetTypeIcon({ asset }: { asset: CatalogWarehouseAsset | CatalogWarehouseAssetDetail }) {
  const isView = asset.name?.toLowerCase().endsWith('_v') || asset.table_external_id?.toLowerCase().endsWith('_v');
  return (
    <span className={cn('inline-flex h-7 w-7 items-center justify-center rounded-lg', isView ? 'bg-violet-50 text-violet-700' : 'bg-indigo-50 text-indigo-700')}>
      {isView ? <Table2 className="h-3.5 w-3.5" /> : <Database className="h-3.5 w-3.5" />}
    </span>
  );
}

function SampleValuesCell({ column }: { column: CatalogColumnDetail }) {
  const status = column.sample_values_status ?? 'not_profiled';
  const values = column.sample_values ?? [];

  if (column.is_pii || status === 'disabled') {
    return (
      <span className="inline-flex items-center gap-1 text-xs italic text-gray-400 dark:text-gray-500">
        <Lock className="h-3 w-3" />
        {column.is_pii ? 'Skipped (PII)' : 'Disabled'}
      </span>
    );
  }

  if (status === 'profiling_failed') {
    return <Badge variant="danger">Profiling failed</Badge>;
  }

  if (values.length) {
    return (
      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap gap-1">
          {values.slice(0, 3).map((value) => (
            <Badge key={value} variant="secondary" className="max-w-[12rem] truncate font-mono">
              {value}
            </Badge>
          ))}
        </div>
        {status === 'stale' && <span className="text-[10px] uppercase tracking-wide text-amber-600">Stale</span>}
      </div>
    );
  }

  return <span className="text-xs italic text-gray-400 dark:text-gray-500">Not profiled</span>;
}

function SemanticLookupCell({ column }: { column: CatalogColumnDetail }) {
  const status = column.value_index_status ?? 'not_indexed';

  if (column.is_pii || column.is_hidden) {
    return (
      <span className="inline-flex items-center gap-1 text-xs italic text-gray-400 dark:text-gray-500">
        <Lock className="h-3 w-3" />
        {column.is_pii ? 'Skipped (PII)' : 'Skipped (hidden)'}
      </span>
    );
  }

  if (!column.semantic_lookup_enabled) {
    if (!column.semantic_lookup_eligible) {
      return (
        <span
          className="text-xs italic text-gray-300 dark:text-gray-600"
          title={column.semantic_lookup_ineligible_reason || 'Not eligible for Semantic Lookup'}
        >
          Not eligible
        </span>
      );
    }
    return <span className="text-xs italic text-gray-400 dark:text-gray-500">Off</span>;
  }

  if (status === 'index_failed') {
    return <Badge variant="danger">Indexing failed</Badge>;
  }
  if (status === 'indexing') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-indigo-600 dark:text-indigo-400">
        <Spinner className="h-3 w-3" />
        Indexing…
      </span>
    );
  }
  if (status === 'indexed') {
    return (
      <div className="flex flex-col gap-1">
        <Badge variant="secondary">
          Indexed{typeof column.value_index_row_count === 'number' ? ` · ${column.value_index_row_count.toLocaleString()} values` : ''}
        </Badge>
        {/* stale reflects the source data possibly drifting since the last index run */}
      </div>
    );
  }
  if (status === 'stale') {
    return <span className="text-[10px] uppercase tracking-wide text-amber-600">Stale</span>;
  }
  // Note: 'disabled' is unreachable here - the toggle-off branch above always
  // catches it first, since the backend sets semantic_lookup_enabled=false in
  // the same request that sets value_index_status='disabled'. "Off" is the
  // single, consistent label for that combined state.
  return <span className="text-xs italic text-gray-400 dark:text-gray-500">Not indexed</span>;
}

function CatalogAssetDetailSurface({
  detail,
  loading,
  onRequestCorrection,
  onBack,
  onRefetch,
  canCreateCorrection,
  canManageWarehouseMetadata,
  canRunCatalogEnrichment,
  canProfileSamples,
}: {
  detail?: CatalogWarehouseAssetDetail;
  loading: boolean;
  onRequestCorrection: (catalogAssetId: number) => void;
  onBack: () => void;
  onRefetch?: () => void;
  canCreateCorrection: boolean;
  canManageWarehouseMetadata: boolean;
  canRunCatalogEnrichment: boolean;
  canProfileSamples: boolean;
}) {
  const enrichAsset = useEnrichCatalogWarehouseAsset(detail?.id);
  const profileSamples = useProfileTableSampleValues(detail?.id);
  const indexValues = useIndexTableValues(detail?.id);
  const updateTableToggle = useUpdateCatalogWarehouseTable(detail?.id);
  const [enrichmentMessage, setEnrichmentMessage] = useState<string | null>(null);
  const [enrichmentFailed, setEnrichmentFailed] = useState(false);
  const [enrichmentRunId, setEnrichmentRunId] = useState<number | null>(null);
  const [sampleMessage, setSampleMessage] = useState<string | null>(null);
  const [profilingTaskId, setProfilingTaskId] = useState<string | null>(null);
  const [indexMessage, setIndexMessage] = useState<string | null>(null);
  const [indexTaskId, setIndexTaskId] = useState<string | null>(null);
  // Scoped per-flow - profiling and indexing hit auth_required independently
  // (e.g. a user can trigger both back-to-back), and a single shared flag
  // would let reconnecting/retrying one flow silently hide the other flow's
  // still-pending reconnect alert.
  const [profileNeedsReconnect, setProfileNeedsReconnect] = useState(false);
  const [indexNeedsReconnect, setIndexNeedsReconnect] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [localEditTarget, setLocalEditTarget] = useState<LocalMetadataEditTarget | null>(null);
  const [selectedColumnIds, setSelectedColumnIds] = useState<Set<number>>(() => new Set());
  const [redraftMenuOpen, setRedraftMenuOpen] = useState(false);
  const enrichmentRun = useCatalogAIEnrichmentRun(enrichmentRunId);
  // Synchronous re-entry guard for handleReconnectBigQuery: the `isReconnecting`
  // *state* only takes effect on the next render, so two clicks fired before
  // React re-renders (or one click on each of the profile/index reconnect
  // buttons) could both pass an `isReconnecting` state check and each register
  // their own `message` listener for the same OAuth popup flow.
  const isReconnectingRef = useRef(false);
  // Tears down a still-in-flight reconnect popup/listener/poll started for a
  // *previous* table before this component's state is reset for a new one -
  // otherwise a late `oauth_complete` postMessage fires the stale closure's
  // runIndexValues/runSampleProfiling + state setters against whichever table
  // is now displayed.
  const reconnectCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    setSelectedColumnIds(new Set());
    setRedraftMenuOpen(false);
    setEnrichmentRunId(null);
    setEnrichmentMessage(null);
    setEnrichmentFailed(false);
    setIndexTaskId(null);
    setIndexMessage(null);
    setProfileNeedsReconnect(false);
    setIndexNeedsReconnect(false);
    setIsReconnecting(false);
    reconnectCleanupRef.current?.();
    reconnectCleanupRef.current = null;
    isReconnectingRef.current = false;
  }, [detail?.id]);

  useEffect(() => {
    const run = enrichmentRun.data;
    if (!run) return;
    const summary = catalogEnrichmentRunSummary(run);
    if (!summary) return;

    setEnrichmentFailed(summary.failed);
    setEnrichmentMessage(summary.message);
    setEnrichmentRunId(null);
    if (summary.shouldRefetch) {
      onRefetch?.();
    }
  }, [enrichmentRun.data, onRefetch]);

  const profilingStream = useProfileSamplesStream({
    tableId: detail?.id ?? null,
    taskId: profilingTaskId,
    onComplete: (event) => {
      const result = (event.result || {}) as { error?: string; message?: string };
      if (result.error === 'auth_required') {
        setProfileNeedsReconnect(true);
        setSampleMessage(
          result.message || 'Reconnect your Google BigQuery account to profile sample values.'
        );
        setProfilingTaskId(null);
        return;
      }
      const indexed = event.summary?.indexed ?? 0;
      const disabled = event.summary?.disabled ?? 0;
      setSampleMessage(
        `Profiled ${indexed} column${indexed === 1 ? '' : 's'}` +
          (disabled ? `, skipped ${disabled} sensitive column${disabled === 1 ? '' : 's'}` : '') +
          '. Sample values are now live below.'
      );
      setProfilingTaskId(null);
      onRefetch?.();
    },
    onError: (event) => {
      setSampleMessage(
        event.event_type === 'timeout'
          ? 'Profiling is taking longer than expected. It may still finish in the background — refresh shortly.'
          : event.error || 'Sample value profiling failed.'
      );
      setProfilingTaskId(null);
    },
  });

  const indexStream = useIndexValuesStream({
    tableId: detail?.id ?? null,
    taskId: indexTaskId,
    onComplete: (event) => {
      const result = (event.result || {}) as { error?: string; message?: string; failed?: number };
      if (result.error === 'auth_required') {
        setIndexNeedsReconnect(true);
        setIndexMessage(
          result.message || 'Reconnect your warehouse account to index Semantic Lookup values.'
        );
        setIndexTaskId(null);
        return;
      }
      const indexed = event.summary?.indexed ?? 0;
      const failed = result.failed ?? 0;
      setIndexMessage(
        `Indexed ${indexed} column${indexed === 1 ? '' : 's'}` +
          (failed ? `, ${failed} failed` : '') +
          '. Semantic Lookup values are now live below.'
      );
      setIndexTaskId(null);
      onRefetch?.();
    },
    onError: (event) => {
      setIndexMessage(
        event.event_type === 'timeout'
          ? 'Indexing is taking longer than expected. It may still finish in the background — refresh shortly.'
          : event.error || 'Semantic Lookup indexing failed.'
      );
      setIndexTaskId(null);
    },
  });

  const isProfiling = profileSamples.isPending || profilingStream.isRunning;
  const isIndexing = indexValues.isPending || indexStream.isRunning;
  const isEnrichmentRunning =
    enrichAsset.isPending ||
    Boolean(enrichmentRunId) ||
    enrichmentRun.data?.status === 'pending' ||
    enrichmentRun.data?.status === 'running';

  const runSampleProfiling = async (force = false, categorical = false) => {
    if (!detail) return;
    setSampleMessage(null);
    setProfileNeedsReconnect(false);
    setProfilingTaskId(null);
    try {
      const result = await profileSamples.mutateAsync({ force, categorical });
      if (result.queued && result.task_id) {
        setProfilingTaskId(result.task_id);
        setSampleMessage(
          'Profiling the most common real values per column. This usually takes a few seconds to a couple of minutes for wide tables.'
        );
        return;
      }
      if (result.queued) {
        setSampleMessage(
          'Sample value profiling queued. The most common real values per column appear here once the run completes.'
        );
        return;
      }
      if (result.error === 'auth_required') {
        setProfileNeedsReconnect(true);
        setSampleMessage(
          result.message || 'Reconnect your Google BigQuery account to profile sample values.'
        );
        return;
      }
      if (result.status === 'error') {
        setSampleMessage(result.error || 'Sample value profiling failed.');
        return;
      }
      setSampleMessage(
        `Profiled ${result.profiled} column${result.profiled === 1 ? '' : 's'}` +
          (result.disabled ? `, skipped ${result.disabled} sensitive column${result.disabled === 1 ? '' : 's'}` : '') +
          '.'
      );
      onRefetch?.();
    } catch {
      // The mutation state drives the visible error alert.
    }
  };

  const runIndexValues = async (force = false) => {
    if (!detail) return;
    setIndexMessage(null);
    setIndexNeedsReconnect(false);
    setIndexTaskId(null);
    try {
      const result = await indexValues.mutateAsync({ force });
      if (result.queued && result.task_id) {
        setIndexTaskId(result.task_id);
        setIndexMessage(
          'Indexing the full distinct value space for each Semantic Lookup column. Large columns can take a while - this runs in the background.'
        );
        return;
      }
      if (result.queued) {
        setIndexMessage('Semantic Lookup indexing queued. Values appear here once the run completes.');
        return;
      }
      if (result.error === 'auth_required') {
        setIndexNeedsReconnect(true);
        setIndexMessage(
          result.message || 'Reconnect your warehouse account to index Semantic Lookup values.'
        );
        return;
      }
      if (result.status === 'error') {
        setIndexMessage(result.message || result.error || 'Semantic Lookup indexing failed.');
        return;
      }
      setIndexMessage(
        `Indexed ${result.indexed} column${result.indexed === 1 ? '' : 's'}` +
          (result.failed ? `, ${result.failed} failed` : '') +
          '.'
      );
      onRefetch?.();
    } catch {
      // The mutation state drives the visible error alert.
    }
  };

  const toggleAutoRefreshSemanticLookup = async (enabled: boolean) => {
    if (!detail) return;
    updateTableToggle.reset();
    try {
      await updateTableToggle.mutateAsync({ auto_refresh_semantic_lookup: enabled });
      onRefetch?.();
    } catch {
      // React Query retains the mutation error for the inline alert below.
    }
  };

  const handleReconnectBigQuery = async (resumeAction: 'profile' | 'index' = 'profile') => {
    // Ref check (not just the `isReconnecting` state, which only takes effect
    // on the next render) so two rapid clicks - or one click on each of the
    // profile/index reconnect buttons - before React re-renders can't both
    // register their own `message` listener for the same OAuth popup.
    if (!detail || isReconnectingRef.current) return;
    isReconnectingRef.current = true;
    setIsReconnecting(true);
    const setResumeMessage = resumeAction === 'index' ? setIndexMessage : setSampleMessage;
    const setResumeNeedsReconnect = resumeAction === 'index' ? setIndexNeedsReconnect : setProfileNeedsReconnect;
    try {
      // Catalog profiling/indexing are connector control-plane operations, not
      // workspace-scoped runtime queries. Reconnect the connector setup grant
      // so the task's setup-credential resolver sees the fresh credentials.
      const { data } = await AXIOS_INSTANCE.get('/oauth/authorize/bigquery', {
        params: {
          connector_config_id: detail.warehouse_connector_id,
          mode: 'connector_setup',
          return_origin: window.location.origin,
        },
      });
      const popup = window.open(data.authorize_url, 'bigquery_oauth', 'width=640,height=760');
      if (!popup) {
        isReconnectingRef.current = false;
        setResumeMessage('Popup blocked. Allow popups for this site, then try reconnecting.');
        setIsReconnecting(false);
        return;
      }

      const cleanup = () => {
        window.removeEventListener('message', handler);
        clearInterval(interval);
        if (!popup.closed) popup.close();
        isReconnectingRef.current = false;
        setIsReconnecting(false);
        if (reconnectCleanupRef.current === cleanup) {
          reconnectCleanupRef.current = null;
        }
      };
      reconnectCleanupRef.current = cleanup;

      const handler = (event: MessageEvent) => {
        if (event.data?.type === 'oauth_complete' && event.data?.source === 'bigquery') {
          cleanup();
          setResumeNeedsReconnect(false);
          // Resume whichever flow actually needed the reconnect - reconnecting
          // from an indexing auth failure must re-trigger indexing, not sample
          // profiling, or the value index stays unbuilt while the user is told
          // the reconnect succeeded.
          if (resumeAction === 'index') {
            setIndexMessage('BigQuery reconnected. Re-indexing Semantic Lookup values…');
            runIndexValues(true);
          } else {
            setSampleMessage('BigQuery reconnected. Re-profiling sample values…');
            runSampleProfiling(true, true);
          }
        }
      };

      window.addEventListener('message', handler);
      const interval = window.setInterval(() => {
        if (popup.closed) cleanup();
      }, 1000);
    } catch (error: any) {
      isReconnectingRef.current = false;
      setIsReconnecting(false);
      setResumeMessage(
        error?.response?.data?.detail || error?.message || 'Failed to start BigQuery reconnect.'
      );
    }
  };

  const runEnrichment = async (payload: CatalogAIEnrichmentPayload = { force: false }) => {
    if (!detail) return;
    setEnrichmentMessage(null);
    setEnrichmentFailed(false);
    setEnrichmentRunId(null);
    setRedraftMenuOpen(false);
    try {
      const run = await enrichAsset.mutateAsync(payload);
      const summary = catalogEnrichmentRunSummary(run);
      if (summary) {
        setEnrichmentFailed(summary.failed);
        setEnrichmentMessage(summary.message);
        if (summary.shouldRefetch) {
          onRefetch?.();
        }
        return;
      }
      setEnrichmentRunId(run.id);
      setEnrichmentMessage(
        `${catalogEnrichmentModeLabel(run.mode || payload.mode || (payload.force ? 'all_columns' : 'missing'))} queued. You can keep working while the catalog worker generates descriptions.`
      );
    } catch {
      // The mutation state drives the visible error alert.
    }
  };

  if (loading) {
    return <CatalogSurface className="flex min-h-[420px] items-center justify-center"><Spinner label="Loading asset detail" /></CatalogSurface>;
  }

  if (!detail) {
    return <CatalogSurface><EmptyState title="Asset not found" description="Choose an asset from the browser to inspect catalog links and field provenance." /></CatalogSurface>;
  }

  const source = sourceForAsset(detail);
  const primaryCatalogAssetId = detail.catalog_links[0]?.catalog_asset.catalog_asset_id;
  const selectableColumnIds = detail.columns.map((column) => column.id);
  const selectedColumnCount = selectedColumnIds.size;
  const allColumnsSelected = selectableColumnIds.length > 0 && selectableColumnIds.every((columnId) => selectedColumnIds.has(columnId));
  const someColumnsSelected = selectedColumnCount > 0 && !allColumnsSelected;
  const toggleColumnSelection = (columnId: number, checked: boolean) => {
    setSelectedColumnIds((current) => {
      const next = new Set(current);
      if (checked) next.add(columnId);
      else next.delete(columnId);
      return next;
    });
  };
  const toggleAllColumnSelection = (checked: boolean) => {
    setSelectedColumnIds(checked ? new Set(selectableColumnIds) : new Set());
  };

  return (
    <>
      <div className="mb-4 flex items-center justify-between gap-4">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-charcoal dark:text-gray-400 dark:hover:text-gray-100">
          <ArrowLeft className="h-4 w-4" />
          Back to assets
        </button>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" size="sm"><Play className="h-4 w-4" />Preview in chat</Button>
          <Button variant="secondary" size="sm"><RefreshCw className="h-4 w-4" />Resync schema</Button>
          {canRunCatalogEnrichment && (
            <Button
              variant="secondary"
              size="sm"
              disabled={isEnrichmentRunning}
              onClick={() => runEnrichment({ force: false })}
            >
              {isEnrichmentRunning ? <Spinner className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
              Draft AI metadata
            </Button>
          )}
          {(canCreateCorrection || canManageWarehouseMetadata) && (
            <Button
              variant="brand"
              size="sm"
              onClick={() => {
                if (primaryCatalogAssetId && canCreateCorrection) {
                  onRequestCorrection(primaryCatalogAssetId);
                  return;
                }
                if (canManageWarehouseMetadata) {
                  setLocalEditTarget({
                    kind: 'table',
                    id: detail.id,
                    title: assetDisplayName(detail),
                    description: detail.local_override_description || detail.source_description || detail.effective_description,
                  });
                }
              }}
            >
              <Pencil className="h-4 w-4" />
              Edit definition
            </Button>
          )}
        </div>
      </div>

      <CatalogSurface className="mb-5">
        <div className="flex items-start justify-between gap-5">
          <div className="flex min-w-0 gap-4">
            <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700">
              <Database className="h-6 w-6" />
            </span>
            <div className="min-w-0">
              <div className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-gray-500">Catalog table</div>
              <h1 className="mt-1 truncate font-title text-3xl leading-tight text-charcoal dark:text-gray-100">{assetDisplayName(detail)}</h1>
              <div className="mt-1 font-mono text-xs text-gray-500 dark:text-gray-400">{detail.qualified_name} · {detail.warehouse_connector_name}</div>
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-2">
            <SourcePill {...source} />
            <div className="flex flex-wrap justify-end gap-2">
              {assetStatusBadge(detail)}
              {detail.pii_column_count > 0 && <Badge variant="warning">PII</Badge>}
            </div>
          </div>
        </div>
        <p className={cn('mt-4 max-w-4xl text-sm leading-6', detail.effective_description ? 'text-charcoal dark:text-gray-100' : 'italic text-gray-400 dark:text-gray-500')}>
          {detail.effective_description || detail.link_status_reason || 'No description yet. Queued for enrichment when the catalog registry is available.'}
        </p>
        <div className="mt-5 flex flex-wrap gap-6 border-t border-gray-200 pt-4 dark:border-dark-border">
          <KV label="Rows"><span className="font-mono">{formatRows(detail.row_count_snapshot)}</span></KV>
          <KV label="Columns"><span className="font-mono">{formatNumber(detail.column_count)}</span></KV>
          <KV label="Freshness"><span>{formatDate(detail.last_synced_at)}</span></KV>
          <KV label="Owner"><span>{detail.catalog_links[0]?.catalog_asset.owners?.join(', ') || 'Not supplied'}</span></KV>
          <KV label="Enrichment"><SourcePill {...source} /></KV>
        </div>
        {canManageWarehouseMetadata && canProfileSamples && (
          <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-3 dark:border-dark-border dark:bg-dark-surface-2">
            <div>
              <div className="text-sm font-medium text-charcoal dark:text-gray-100">Auto-refresh semantic values</div>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                Hourly scan re-indexes this table&apos;s Semantic Lookup columns when their value index goes stale.
                Separate from connector sync scheduling.
              </p>
              {updateTableToggle.isError && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
                  Auto-refresh could not be updated. Check your connection and try again.
                </p>
              )}
            </div>
            <label className="inline-flex shrink-0 items-center gap-2">
              <input
                type="checkbox"
                aria-label="Auto-refresh semantic values"
                className="h-4 w-4 rounded border-gray-300"
                checked={Boolean(detail.auto_refresh_semantic_lookup)}
                disabled={updateTableToggle.isPending}
                onChange={(event) => toggleAutoRefreshSemanticLookup(event.target.checked)}
              />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {detail.auto_refresh_semantic_lookup ? 'On' : 'Off'}
              </span>
            </label>
          </div>
        )}
      </CatalogSurface>

      {(enrichmentMessage || enrichAsset.isError || enrichmentRun.isError) && (
        <Alert
          className="mb-5"
          variant={enrichmentFailed || enrichAsset.isError || enrichmentRun.isError ? 'error' : 'info'}
          title={enrichmentFailed || enrichAsset.isError || enrichmentRun.isError ? 'AI enrichment failed' : 'AI enrichment'}
        >
          {enrichAsset.isError
            ? 'The catalog worker could not be queued. Confirm Celery is running, then try again.'
            : enrichmentRun.isError
              ? 'Unable to refresh AI enrichment status. Refresh this page to check the latest run.'
              : enrichmentMessage}
        </Alert>
      )}

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
        <CatalogSurface padded={false}>
          <SectionHeader
            title="Columns"
            description="Click any field to propose a correction. Sample values are queried from the source database (never AI-generated) and ground SQL literal generation."
            action={
              <div className="flex flex-wrap gap-2">
                {canProfileSamples && (
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={isProfiling}
                    onClick={() => runSampleProfiling(true, true)}
                    title="Query the 3 most common real values for each column from the source database."
                  >
                    {isProfiling ? <Spinner className="h-4 w-4" /> : <FlaskConical className="h-4 w-4" />}
                    {profilingStream.isRunning ? 'Profiling…' : 'Profile sample values'}
                  </Button>
                )}
                {canProfileSamples && (
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={isIndexing}
                    onClick={() => runIndexValues(true)}
                    title="Index the full distinct value space for every Semantic-Lookup-enabled column."
                  >
                    {isIndexing ? <Spinner className="h-4 w-4" /> : <Search className="h-4 w-4" />}
                    {indexStream.isRunning ? 'Indexing…' : 'Index values'}
                  </Button>
                )}
                {canRunCatalogEnrichment && (
                  <div className="relative">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={isEnrichmentRunning}
                      onClick={() => setRedraftMenuOpen((open) => !open)}
                      aria-haspopup="menu"
                      aria-expanded={redraftMenuOpen}
                    >
                      {isEnrichmentRunning ? <Spinner className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
                      Redraft
                      <ChevronDown className="h-3.5 w-3.5" />
                    </Button>
                    {redraftMenuOpen && (
                      <div
                        role="menu"
                        className="absolute right-0 z-20 mt-2 w-64 rounded-lg border border-gray-200 bg-white p-1 shadow-lg dark:border-dark-border dark:bg-dark-surface"
                      >
                        <Button
                          type="button"
                          role="menuitem"
                          variant="ghost"
                          size="sm"
                          className="w-full justify-start rounded-md px-3 text-left"
                          onClick={() => runEnrichment({ force: true, mode: 'table' })}
                        >
                          <Table2 className="h-4 w-4 text-gray-500" />
                          Redraft table description
                        </Button>
                        <Button
                          type="button"
                          role="menuitem"
                          disabled={selectedColumnCount === 0}
                          variant="ghost"
                          size="sm"
                          className={cn(
                            'w-full justify-start rounded-md px-3 text-left',
                            selectedColumnCount === 0 && 'text-gray-400'
                          )}
                          onClick={() => {
                            if (selectedColumnCount === 0) return;
                            runEnrichment({
                              force: true,
                              mode: 'selected_columns',
                              column_ids: Array.from(selectedColumnIds),
                            });
                          }}
                        >
                          <CheckCircle2 className="h-4 w-4 text-gray-500" />
                          Redraft selected columns
                        </Button>
                        <Button
                          type="button"
                          role="menuitem"
                          variant="ghost"
                          size="sm"
                          className="w-full justify-start rounded-md px-3 text-left"
                          onClick={() => runEnrichment({ force: true, mode: 'all_columns' })}
                        >
                          <Sparkles className="h-4 w-4 text-gray-500" />
                          Redraft all columns
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            }
          />
          {(sampleMessage || profileSamples.isError) && (
            <div className="px-4 pt-3">
              <Alert
                variant={profileSamples.isError ? 'error' : profileNeedsReconnect ? 'warning' : 'info'}
                title={
                  profileSamples.isError
                    ? 'Sample value profiling failed'
                    : profileNeedsReconnect
                      ? 'Reconnect Google BigQuery'
                      : 'Sample values'
                }
              >
                <div className="flex flex-col gap-2">
                  <span>
                    {profileSamples.isError
                      ? 'The profiling run could not complete. Confirm the warehouse connector is reachable, then try again.'
                      : sampleMessage}
                  </span>
                  {profileNeedsReconnect && (
                    <div>
                      <Button
                        variant="brand"
                        size="sm"
                        disabled={isReconnecting}
                        onClick={() => handleReconnectBigQuery('profile')}
                      >
                        {isReconnecting ? <Spinner className="h-4 w-4" /> : <Database className="h-4 w-4" />}
                        Reconnect Google BigQuery
                      </Button>
                    </div>
                  )}
                </div>
              </Alert>
            </div>
          )}
          {(indexMessage || indexValues.isError) && (
            <div className="px-4 pt-3">
              <Alert
                variant={indexValues.isError ? 'error' : indexNeedsReconnect ? 'warning' : 'info'}
                title={
                  indexValues.isError
                    ? 'Semantic Lookup indexing failed'
                    : indexNeedsReconnect
                      ? 'Reconnect warehouse account'
                      : 'Semantic Lookup'
                }
              >
                <div className="flex flex-col gap-2">
                  <span>
                    {indexValues.isError
                      ? 'The indexing run could not complete. Confirm the warehouse connector is reachable, then try again.'
                      : indexMessage}
                  </span>
                  {indexNeedsReconnect && (
                    <div>
                      <Button
                        variant="brand"
                        size="sm"
                        disabled={isReconnecting}
                        onClick={() => handleReconnectBigQuery('index')}
                      >
                        {isReconnecting ? <Spinner className="h-4 w-4" /> : <Database className="h-4 w-4" />}
                        Reconnect warehouse account
                      </Button>
                    </div>
                  )}
                </div>
              </Alert>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse">
              <thead className="bg-gray-50 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-400 dark:bg-dark-surface-2 dark:text-gray-500">
                <tr className="border-b border-gray-200 dark:border-dark-border">
                  <th className="w-10 px-4 py-3">
                    <Checkbox
                      aria-label="Select all columns"
                      checked={allColumnsSelected}
                      indeterminate={someColumnsSelected}
                      onChange={(event) => toggleAllColumnSelection(event.target.checked)}
                    />
                  </th>
                  <th className="px-4 py-3">Column</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Description</th>
                  <th className="px-4 py-3">Sample values</th>
                  <th className="px-4 py-3">Semantic Lookup</th>
                  <th className="px-4 py-3">Catalog enrichment</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {detail.columns.length ? detail.columns.map((column) => {
                  const columnSource = column.catalog_links[0]
                    ? sourceForAsset({ ...detail, catalog_links: column.catalog_links })
                    : { source: 'warehouse' as SourceKind, label: providerLabel(detail.warehouse_type) };
                  return (
                    <tr key={column.id} className="border-b border-gray-100 hover:bg-gray-50 dark:border-dark-border dark:hover:bg-dark-surface-2">
                      <td className="px-4 py-3 align-top">
                        <Checkbox
                          aria-label={`Select ${column.name}`}
                          checked={selectedColumnIds.has(column.id)}
                          onChange={(event) => toggleColumnSelection(column.id, event.target.checked)}
                        />
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="font-mono text-sm text-charcoal dark:text-gray-100">{column.name}</div>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {column.is_pii && <Badge variant="warning">PII</Badge>}
                          {column.is_hidden && <Badge variant="secondary">Hidden</Badge>}
                          {column.effective_alias && <Badge variant="outline">{column.effective_alias}</Badge>}
                          {(column.governed_aliases ?? []).slice(0, 3).map((alias) => (
                            <Badge key={alias} variant="outline">{alias}</Badge>
                          ))}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 align-top font-mono text-xs text-gray-500 dark:text-gray-400">{column.data_type}</td>
                      <td className={cn('max-w-xl px-4 py-3 align-top text-sm leading-5', column.effective_description ? 'text-charcoal dark:text-gray-100' : 'italic text-gray-400 dark:text-gray-500')}>
                        {column.effective_description || 'No description yet. Use Draft AI metadata to fill blank fields.'}
                      </td>
                      <td className="px-4 py-3 align-top"><SampleValuesCell column={column} /></td>
                      <td className="px-4 py-3 align-top"><SemanticLookupCell column={column} /></td>
                      <td className="px-4 py-3 align-top"><SourcePill {...columnSource} /></td>
                      <td className="px-4 py-3 align-top">
                        {(canCreateCorrection || canManageWarehouseMetadata) && (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => {
                              const columnCatalogAssetId = column.catalog_links[0]?.catalog_asset.catalog_asset_id;
                              if (columnCatalogAssetId && canCreateCorrection) {
                                onRequestCorrection(columnCatalogAssetId);
                                return;
                              }
                              if (canManageWarehouseMetadata) {
                                setLocalEditTarget({
                                  kind: 'column',
                                  id: column.id,
                                  title: column.name,
                                  description: column.local_override_description || column.source_description || column.effective_description,
                                  alias: column.effective_alias,
                                  aliases: column.governed_aliases ?? [],
                                  isPii: column.is_pii,
                                  sampleValuesStatus: column.sample_values_status,
                                  semanticLookupEnabled: column.semantic_lookup_enabled,
                                  semanticLookupEligible: column.semantic_lookup_eligible,
                                  semanticLookupIneligibleReason: column.semantic_lookup_ineligible_reason,
                                });
                              }
                            }}
                            aria-label={`Edit ${column.name}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                }) : (
                  <tr><td colSpan={8}><EmptyState title="No columns loaded" description="Column-level detail loads on demand from the warehouse registry." /></td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CatalogSurface>

        <div className="space-y-4">
          <SidePanel title="Catalog provenance">
            <ProvenanceRows links={detail.catalog_links} source={source} />
          </SidePanel>
          <SidePanel title="Used by">
            <div className="text-sm italic text-gray-400 dark:text-gray-500">Workspace usage is not exposed by the current catalog contract.</div>
          </SidePanel>
          <SidePanel title="Lineage">
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-500 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-400">
              <GitBranch className="mx-auto mb-2 h-7 w-7" />
              Provider lineage renders here when a lineage-capable source is connected.
            </div>
          </SidePanel>
          <SidePanel title="AI enrichment">
            <div className="space-y-2 text-sm text-charcoal dark:text-gray-100">
              <div className="flex justify-between"><span className="text-gray-500 dark:text-gray-400">Mode</span><Badge variant="secondary">Per table</Badge></div>
              <div className="flex justify-between"><span className="text-gray-500 dark:text-gray-400">Default</span><span>Skip existing metadata</span></div>
              {canRunCatalogEnrichment && (
                <Button variant="secondary" size="sm" className="mt-2 w-full" disabled={isEnrichmentRunning} onClick={() => runEnrichment({ force: false })}>
                  {isEnrichmentRunning ? <Spinner className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
                  Draft missing metadata
                </Button>
              )}
            </div>
          </SidePanel>
        </div>
      </div>
      {localEditTarget && detail && (
        <LocalMetadataDialog
          assetId={detail.id}
          target={localEditTarget}
          onClose={() => setLocalEditTarget(null)}
        />
      )}
    </>
  );
}

function KV({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-[110px]">
      <div className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-gray-500">{label}</div>
      <div className="mt-1 text-sm text-charcoal dark:text-gray-100">{children}</div>
    </div>
  );
}

function SidePanel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <CatalogSurface className="p-4">
      <div className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-gray-500">{title}</div>
      <div className="mt-3">{children}</div>
    </CatalogSurface>
  );
}

function ProvenanceRows({ links, source }: { links: CatalogLinkSummary[]; source: { source: SourceKind; label: string; confidence?: number } }) {
  if (!links.length) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300">
        No catalog provider record is linked yet.
      </div>
    );
  }
  const link = links[0];
  return (
    <div className="space-y-2 text-sm">
      <div className="flex items-center justify-between gap-4"><span className="text-gray-500 dark:text-gray-400">Description</span><SourcePill {...source} /></div>
      <div className="flex items-center justify-between gap-4"><span className="text-gray-500 dark:text-gray-400">Resolver</span><span className="text-charcoal dark:text-gray-100">{link.resolver_method} · {Math.round(link.confidence_score * 100)}%</span></div>
      <div className="flex items-center justify-between gap-4"><span className="text-gray-500 dark:text-gray-400">Owners</span><span className="text-right text-charcoal dark:text-gray-100">{link.catalog_asset.owners?.join(', ') || 'Not supplied'}</span></div>
      <div className="flex items-center justify-between gap-4"><span className="text-gray-500 dark:text-gray-400">Synced</span><span className="text-charcoal dark:text-gray-100">{formatDate(link.catalog_asset.source_last_updated_at)}</span></div>
    </div>
  );
}

function CatalogCorrectionsSurface({ corrections }: { corrections: CatalogCorrection[] }) {
  const [filter, setFilter] = useState<'pending' | 'approved' | 'rejected' | 'all'>('pending');
  const [selectedId, setSelectedId] = useState<number | undefined>(corrections[0]?.id);
  const list = corrections.filter((correction) => {
    if (filter === 'all') return true;
    if (filter === 'pending') return correction.status === 'pending' || correction.status === 'pending_review' || correction.status === 'needs_clarification';
    if (filter === 'approved') return correction.status.includes('approved');
    if (filter === 'rejected') return correction.status.includes('rejected');
    return true;
  });
  const selected = corrections.find((correction) => correction.id === selectedId) || list[0];

  return (
    <>
      <AppletHeader
        eyebrow="Catalog · Corrections queue"
        title={`${formatNumber(list.length)} corrections need review`}
        description="Workspace users flag inaccurate metadata; stewards review, dedupe, and approve here. Provider writeback remains governed."
        actions={
          <>
            <Button variant="secondary" size="sm"><Filter className="h-4 w-4" />Filters</Button>
            <Button variant="secondary" size="sm" disabled><Check className="h-4 w-4" />Bulk approve</Button>
          </>
        }
      />

      <div className="mb-4 flex flex-wrap gap-2">
        <FilterChip active={filter === 'pending'} onClick={() => setFilter('pending')}>Pending · {corrections.length}</FilterChip>
        <FilterChip active={filter === 'approved'} onClick={() => setFilter('approved')}>Approved · 0</FilterChip>
        <FilterChip active={filter === 'rejected'} onClick={() => setFilter('rejected')}>Rejected · 0</FilterChip>
        <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>All · {corrections.length}</FilterChip>
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.6fr_1fr]">
        <div>
          {list.length ? list.map((correction) => (
            <button
              key={correction.id}
              type="button"
              onClick={() => setSelectedId(correction.id)}
              className={cn(
                'mb-3 grid w-full grid-cols-[32px_1fr] gap-3 rounded-2xl border bg-white p-4 text-left transition-colors dark:bg-dark-surface',
                selected?.id === correction.id ? 'border-eliza-red/50 shadow-sm' : 'border-gray-200 hover:border-gray-300 dark:border-dark-border'
              )}
            >
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-50 text-amber-700">
                <AlertTriangle className="h-4 w-4" />
              </span>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs text-charcoal dark:text-gray-100">{correction.catalog_asset?.display_name || correction.catalog_asset?.name || correction.field_path}</span>
                  <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] text-gray-500 dark:bg-dark-surface-2">{correction.field_path}</span>
                  <Badge variant={correction.catalog_asset?.provider_type === 'atlan' ? 'info' : 'secondary'}>
                    {correction.catalog_asset?.provider_type === 'atlan' ? 'Atlan-owned' : 'Eliza-native'}
                  </Badge>
                  {correctionSourceLabel(correction) ? <Badge variant="warning">{correctionSourceLabel(correction)}</Badge> : null}
                </div>
                <div className="mt-2 rounded-xl border border-gray-200 bg-gray-50 p-3 font-mono text-xs dark:border-dark-border dark:bg-dark-surface-2">
                  <div className="flex gap-2 text-gray-500"><span className="text-rose-600">-</span><span>{correction.original_value || '(empty)'}</span></div>
                  <div className="mt-1 flex gap-2 text-charcoal dark:text-gray-100"><span className="text-emerald-600">+</span><span>{correction.proposed_value}</span></div>
                </div>
                <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <span>{formatDate(correction.created_at)}</span>
                  <span>· status {correction.status}</span>
                  {correction.reason && <span>· {correction.reason}</span>}
                </div>
              </div>
            </button>
          )) : (
            <CatalogSurface><EmptyState title="No corrections in this view" description="Approved and rejected history is not returned by the current pending-queue endpoint." /></CatalogSurface>
          )}
        </div>
        {selected ? <CorrectionDetail correction={selected} /> : <CatalogSurface><EmptyState title="No correction selected" description="Choose a correction to inspect review details." /></CatalogSurface>}
      </div>
    </>
  );
}

function correctionSourceLabel(correction: CatalogCorrection): string | null {
  const metadata = correction.source_metadata;
  if (!metadata || typeof metadata !== 'object') return null;
  const workspace = typeof metadata.workspace_name === 'string' ? metadata.workspace_name : null;
  const source = typeof metadata.source_surface === 'string' ? metadata.source_surface : null;
  if (workspace && source) return `${workspace} · ${source.replace(/_/g, ' ')}`;
  if (workspace) return workspace;
  if (source) return source.replace(/_/g, ' ');
  return null;
}

function correctionSourceDetails(correction: CatalogCorrection): Array<[string, string]> {
  const metadata = correction.source_metadata;
  if (!metadata || typeof metadata !== 'object') return [];
  const entries: Array<[string, string]> = [];
  const proposalId = typeof metadata.proposal_id === 'number' || typeof metadata.proposal_id === 'string'
    ? String(metadata.proposal_id)
    : null;
  const packVersion = typeof metadata.active_pack_version_at_submission === 'number' || typeof metadata.active_pack_version_at_submission === 'string'
    ? String(metadata.active_pack_version_at_submission)
    : null;
  const correctionKind = typeof metadata.correction_kind === 'string' ? metadata.correction_kind.replace(/_/g, ' ') : null;
  const traceId = typeof metadata.trace_id === 'string' ? metadata.trace_id : null;
  if (proposalId) entries.push(['Workspace proposal', `#${proposalId}`]);
  if (correctionKind) entries.push(['Correction kind', correctionKind]);
  if (packVersion) entries.push(['Submitted pack', `v${packVersion}`]);
  if (traceId) entries.push(['Trace', traceId]);
  return entries;
}

function CorrectionDetail({ correction }: { correction: CatalogCorrection }) {
  const sourceLabel = correctionSourceLabel(correction);
  const sourceDetails = correctionSourceDetails(correction);
  return (
    <CatalogSurface className="sticky top-5 max-h-[calc(100vh-120px)] overflow-hidden" padded={false}>
      <div className="border-b border-gray-200 px-5 py-4 dark:border-dark-border">
        <div className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-gray-500">
          {correction.catalog_asset?.provider_type === 'atlan' ? 'Atlan correction' : 'Eliza-native correction'}
        </div>
        <h2 className="mt-1 font-subtitle text-xl text-charcoal dark:text-gray-100">Review proposal</h2>
        <div className="mt-1 break-all font-mono text-xs text-gray-500 dark:text-gray-400">{correction.field_path}</div>
      </div>
      <div className="max-h-[calc(100vh-250px)] space-y-4 overflow-y-auto p-5">
        <ReviewBlock title="Field" value={correction.field_path} />
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-dark-border dark:bg-dark-surface-2">
          <div className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-400">Current</div>
          <div className="mt-1 text-sm text-charcoal dark:text-gray-100">{correction.original_value || '(empty)'}</div>
          <div className="mt-4 text-xs font-semibold uppercase tracking-[0.08em] text-emerald-600">Proposed</div>
          <div className="mt-1 text-sm text-charcoal dark:text-gray-100">{correction.proposed_value}</div>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between gap-4"><span className="text-gray-500 dark:text-gray-400">Authority</span><Badge variant={correction.catalog_asset?.provider_type === 'atlan' ? 'info' : 'secondary'}>{providerLabel(correction.catalog_asset?.provider_type)}</Badge></div>
          <div className="flex justify-between gap-4"><span className="text-gray-500 dark:text-gray-400">Writeback</span><span className="text-charcoal dark:text-gray-100">{correction.catalog_asset?.provider_type === 'atlan' ? 'Eligible after approval' : 'Not applicable'}</span></div>
          <div className="flex justify-between gap-4"><span className="text-gray-500 dark:text-gray-400">Status</span><Badge variant="warning">{correction.status}</Badge></div>
        </div>
        {correction.reason && <ReviewBlock title="Reason" value={correction.reason} />}
        {sourceLabel ? <ReviewBlock title="Origin" value={sourceLabel} /> : null}
        {sourceDetails.length > 0 ? (
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-dark-border dark:bg-dark-surface-2">
            <div className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-400">Workspace handoff</div>
            <div className="mt-3 space-y-2 text-sm">
              {sourceDetails.map(([label, value]) => (
                <div key={label} className="flex justify-between gap-4">
                  <span className="text-gray-500 dark:text-gray-400">{label}</span>
                  <span className="break-all text-right text-charcoal dark:text-gray-100">{value}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
        {correction.reviewer_notes && <ReviewBlock title="Reviewer notes" value={correction.reviewer_notes} />}
        <ReviewBlock title="Activity" value={`Created ${formatDate(correction.created_at)}${correction.reviewed_at ? ` · reviewed ${formatDate(correction.reviewed_at)}` : ''}`} />
      </div>
      <div className="flex flex-wrap gap-2 border-t border-gray-200 bg-gray-50 px-5 py-3 dark:border-dark-border dark:bg-dark-surface-2">
        <Button variant="ghost" size="sm" disabled>Mark duplicate</Button>
        <Button variant="destructive" size="sm" disabled>Reject</Button>
        <span className="flex-1" />
        <Button variant="brand" size="sm" disabled><Check className="h-4 w-4" />{correction.catalog_asset?.provider_type === 'atlan' ? 'Approve & writeback' : 'Approve'}</Button>
      </div>
    </CatalogSurface>
  );
}

function ReviewBlock({ title, value }: { title: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-gray-500">{title}</div>
      <div className="mt-1 text-sm leading-6 text-charcoal dark:text-gray-100">{value}</div>
    </div>
  );
}

function CatalogProvidersSurface({
  overview,
  providers,
}: {
  overview?: ReturnType<typeof useCatalogOverview>['data'];
  providers: CatalogProviderSummary[];
}) {
  const navigate = useNavigate();
  const nonNativeProviders = providers.filter((provider) => provider.provider_type !== 'eliza_native');
  const connectedTypes = new Set(nonNativeProviders.map((provider) => provider.provider_type));

  return (
    <>
      <AppletHeader
        eyebrow="Catalog · Providers"
        title="Catalog enrichment providers"
        description="Eliza-native is the always-on backbone. Optional providers layer in third-party metadata, lineage, and governed writeback."
        actions={<Button variant="brand" size="sm" onClick={() => navigate('/data-connections?category=catalog')}><Plus className="h-4 w-4" />Connect provider</Button>}
      />
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <ProviderCard
          name="Eliza Platform Catalog"
          type="eliza_native"
          status="Always on"
          description="Built-in catalog backbone for warehouse schema, local overrides, resolver state, and workspace consumption."
          stats={[
            ['Assets indexed', formatNumber(overview?.total_warehouse_assets)],
            ['Linked assets', formatNumber(overview?.linked_warehouse_assets)],
            ['Glossary terms', formatNumber(overview?.glossary_terms)],
          ]}
          native
        />
        {nonNativeProviders.map((provider) => (
          <ProviderCard
            key={`${provider.provider_type}-${provider.connector_id || provider.connector_name}`}
            name={provider.connector_name}
            type={provider.provider_type}
            status={provider.status}
            description={`${providerLabel(provider.provider_type)} provider metadata is ${provider.is_healthy ? 'healthy' : 'not reporting healthy status'}.`}
            stats={[
              ['Coverage', `${formatNumber(provider.linked_warehouse_asset_count)}/${formatNumber(overview?.total_warehouse_assets)}`],
              ['Last sync', formatDate(provider.last_synced_at)],
              ['Writeback', provider.writeback_supported ? 'Governed' : 'Read-only'],
            ]}
            connected
          />
        ))}
        {availableProviders.filter((provider) => !connectedTypes.has(provider.type) && !connectedTypes.has(provider.key)).map((provider) => (
          <ProviderCard
            key={provider.key}
            name={provider.name}
            type={provider.type}
            status="Available"
            description={provider.description}
            action={<Button variant="secondary" size="sm" onClick={() => navigate('/data-connections?category=catalog')}><Plus className="h-4 w-4" />Connect via Data Connections</Button>}
          />
        ))}
      </div>
    </>
  );
}

function ProviderCard({
  name,
  type,
  status,
  description,
  stats,
  action,
  native,
  connected,
}: {
  name: string;
  type: string;
  status: string;
  description: string;
  stats?: [string, string][];
  action?: React.ReactNode;
  native?: boolean;
  connected?: boolean;
}) {
  return (
    <CatalogSurface className={cn(native && 'border-eliza-red/40 bg-gradient-to-br from-eliza-red/5 to-white', connected && 'border-eliza-red/20')}>
      <div className="flex items-start gap-3">
        <span className={cn('inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', native ? 'bg-eliza-red text-white' : 'bg-gray-100 text-gray-500 dark:bg-dark-surface-2 dark:text-gray-300')}>
          {native ? <Sparkles className="h-5 w-5" /> : <BookOpen className="h-5 w-5" />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-subtitle text-lg text-charcoal dark:text-gray-100">{name}</div>
          <div className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-gray-500">{providerLabel(type)}</div>
        </div>
        <Badge variant={native || connected ? 'success' : 'secondary'}>{status}</Badge>
      </div>
      <p className="mt-3 min-h-[42px] text-sm leading-6 text-gray-500 dark:text-gray-400">{description}</p>
      {stats && (
        <div className="mt-4 grid grid-cols-3 gap-3 border-t border-gray-200 pt-3 text-xs text-gray-500 dark:border-dark-border dark:text-gray-400">
          {stats.map(([label, value]) => (
            <div key={label}>
              {label}<br />
              <b className="font-subtitle text-base font-normal text-charcoal dark:text-gray-100">{value}</b>
            </div>
          ))}
        </div>
      )}
      <div className="mt-4 flex gap-2">
        {action || (connected ? (
          <>
            <Button variant="ghost" size="sm"><RefreshCw className="h-4 w-4" />Sync</Button>
            <Button variant="ghost" size="sm"><Settings className="h-4 w-4" />Configure</Button>
          </>
        ) : native ? <Button variant="ghost" size="sm">Manage</Button> : null)}
      </div>
    </CatalogSurface>
  );
}

function CatalogSettingsSurface({ overview }: { overview?: ReturnType<typeof useCatalogOverview>['data'] }) {
  return (
    <>
      <AppletHeader
        eyebrow="Catalog · Settings"
        title="Catalog policy"
        description="Set defaults for field authority, AI enrichment, writeback, sync cadence, and conflict resolution. Controls are read-only until broader policy endpoints land."
      />
      <div className="grid gap-4 xl:grid-cols-2">
        <PolicyCard title="AI enrichment" subtitle="Auto-cataloging">
          <p className="text-sm leading-6 text-gray-500 dark:text-gray-400">AI drafts missing descriptions and aliases for one table at a time. Existing metadata is skipped unless a steward forces a re-draft.</p>
          <PolicyRow label="Status"><Badge variant={overview?.registry_available === false ? 'secondary' : 'success'}>{overview?.registry_available === false ? 'Unavailable' : 'Enabled'}</Badge></PolicyRow>
          <PolicyRow label="Model"><code className="font-mono text-sm">catalog.asset_enrichment</code></PolicyRow>
          <PolicyRow label="Coverage">{Math.round(overview?.coverage_percentage || 0)}%</PolicyRow>
        </PolicyCard>
        <PolicyCard title="Field authority defaults" subtitle="Authority precedence">
          {['Local steward override', 'Atlan or connected provider', 'Eliza-native human edit', 'Eliza-native AI draft', 'Warehouse schema'].map((item, index) => (
            <div key={item} className="mb-2 flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 dark:border-dark-border dark:bg-dark-surface-2">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-xs font-semibold text-gray-500 dark:bg-dark-surface">{index + 1}</span>
              <span className="text-sm text-charcoal dark:text-gray-100">{item}</span>
            </div>
          ))}
        </PolicyCard>
        <PolicyCard title="Writeback policy" subtitle="Push approved changes upstream">
          <PolicyRow label="Provider writeback"><Badge variant="secondary">Governed by provider</Badge></PolicyRow>
          <PolicyRow label="Approver role">Data Steward, Data Connection Admin</PolicyRow>
          <PolicyRow label="Eligible fields">description, alias, owner, classification</PolicyRow>
          <PolicyRow label="On conflict">Prefer local override; flag for steward</PolicyRow>
        </PolicyCard>
        <PolicyCard title="Sync" subtitle="Refresh cadence">
          <PolicyRow label="Warehouse schema">Current connector cadence</PolicyRow>
          <PolicyRow label="Catalog providers">Provider-specific cadence</PolicyRow>
          <PolicyRow label="AI enrichment">Manual per-table; skips existing metadata</PolicyRow>
          <Button variant="secondary" size="sm" disabled><RefreshCw className="h-4 w-4" />Run all syncs now</Button>
        </PolicyCard>
      </div>
    </>
  );
}

function PolicyCard({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <CatalogSurface>
      <div className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-gray-500">{title}</div>
      <h3 className="mt-1 font-subtitle text-xl text-charcoal dark:text-gray-100">{subtitle}</h3>
      <div className="mt-4 space-y-3">{children}</div>
    </CatalogSurface>
  );
}

function PolicyRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 text-sm">
      <span className="text-gray-500 dark:text-gray-400">{label}</span>
      <span className="text-right text-charcoal dark:text-gray-100">{children}</span>
    </div>
  );
}

function LocalMetadataDialog({
  assetId,
  target,
  onClose,
}: {
  assetId: number;
  target: LocalMetadataEditTarget;
  onClose: () => void;
}) {
  const updateTable = useUpdateCatalogWarehouseTable(assetId);
  const updateColumn = useUpdateCatalogWarehouseColumn(assetId);
  const [description, setDescription] = useState(target.description || '');
  const [aliases, setAliases] = useState(
    target.kind === 'column'
      ? [target.alias, ...(target.aliases ?? [])].filter(Boolean).join(', ')
      : '',
  );
  const initialSampleEnabled =
    target.kind === 'column' ? target.sampleValuesStatus !== 'disabled' : true;
  const [sampleValuesEnabled, setSampleValuesEnabled] = useState(initialSampleEnabled);
  const initialSemanticLookupEnabled =
    target.kind === 'column' ? Boolean(target.semanticLookupEnabled) : false;
  const [semanticLookupEnabled, setSemanticLookupEnabled] = useState(initialSemanticLookupEnabled);
  const isPending = updateTable.isPending || updateColumn.isPending;
  const isError = updateTable.isError || updateColumn.isError;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (target.kind === 'table') {
      await updateTable.mutateAsync({
        local_override_description: description.trim() || null,
      });
    } else {
      const aliasList = normalizeAliasInput(aliases).slice(0, 12);
      await updateColumn.mutateAsync({
        columnId: target.id,
        local_override_description: description.trim() || null,
        effective_alias: aliasList[0] || null,
        governed_aliases: aliasList,
        ...(sampleValuesEnabled !== initialSampleEnabled
          ? { sample_values_enabled: sampleValuesEnabled }
          : {}),
        ...(semanticLookupEnabled !== initialSemanticLookupEnabled
          ? { semantic_lookup_enabled: semanticLookupEnabled }
          : {}),
      });
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-4 backdrop-blur-sm">
      <form onSubmit={submit} className="w-full max-w-xl overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-dark-border dark:bg-dark-surface">
        <div className="border-b border-gray-200 px-6 py-5 dark:border-dark-border">
          <div className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-gray-500">Edit local catalog metadata</div>
          <h2 className="mt-1 font-title text-2xl text-charcoal dark:text-gray-100">{target.title}</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Saves an Eliza-native local override for this warehouse {target.kind}. Provider writeback is only used when a provider catalog asset is linked.
          </p>
        </div>
        <div className="space-y-4 px-6 py-5">
          <label className="block">
            <span className="text-sm font-medium text-charcoal dark:text-gray-100">Description</span>
            <Textarea
              className="mt-1 min-h-[120px]"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </label>
          {target.kind === 'column' && (
            <label className="block">
              <span className="text-sm font-medium text-charcoal dark:text-gray-100">Aliases</span>
              <Input className="mt-1" value={aliases} onChange={(event) => setAliases(event.target.value)} />
            </label>
          )}
          {target.kind === 'column' && (
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-gray-300"
                checked={sampleValuesEnabled}
                disabled={target.isPii}
                onChange={(event) => setSampleValuesEnabled(event.target.checked)}
              />
              <span className="text-sm">
                <span className="font-medium text-charcoal dark:text-gray-100">Allow sample value profiling</span>
                <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                  {target.isPii
                    ? 'PII columns are always skipped from sample value profiling.'
                    : 'Disable to keep DB-queried example values out of the catalog and SQL prompts for this column.'}
                </span>
              </span>
            </label>
          )}
          {target.kind === 'column' && (
            <label className="flex items-start gap-3 rounded-lg border border-indigo-100 bg-indigo-50/60 px-3 py-2.5 dark:border-indigo-900/40 dark:bg-indigo-950/20">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-indigo-300 text-indigo-600"
                checked={semanticLookupEnabled}
                disabled={!target.semanticLookupEligible && !semanticLookupEnabled}
                onChange={(event) => setSemanticLookupEnabled(event.target.checked)}
              />
              <span className="text-sm">
                <span className="font-medium text-charcoal dark:text-gray-100">Semantic Lookup</span>
                <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                  {!target.semanticLookupEligible
                    ? target.semanticLookupIneligibleReason || 'Not eligible for Semantic Lookup.'
                    : 'Indexes every distinct value in this column for RAG-based lookup at query time - a separate mechanism from sample value profiling above. Use "Index values" after enabling.'}
                </span>
              </span>
            </label>
          )}
          {isError && (
            <Alert variant="error" title="Metadata save failed">
              Confirm you have connector write access and try again.
            </Alert>
          )}
        </div>
        <div className="flex justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4 dark:border-dark-border dark:bg-dark-surface-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="brand" disabled={isPending}>
            {isPending ? <Spinner className="h-4 w-4" /> : <Check className="h-4 w-4" />}
            Save local metadata
          </Button>
        </div>
      </form>
    </div>
  );
}

function normalizeAliasInput(value: string): string[] {
  const seen = new Set<string>();
  const aliases: string[] = [];
  value
    .split(/[\n,]/)
    .map((alias) => alias.trim())
    .forEach((alias) => {
      const key = alias.toLowerCase();
      if (!alias || seen.has(key)) return;
      seen.add(key);
      aliases.push(alias);
    });
  return aliases;
}

function CorrectionDialog({
  catalogAssetId,
  onClose,
}: {
  catalogAssetId: number;
  onClose: () => void;
}) {
  const createCorrection = useCreateCatalogCorrection();
  const [fieldPath, setFieldPath] = useState('description');
  const [proposedValue, setProposedValue] = useState('');
  const [reason, setReason] = useState('');

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    await createCorrection.mutateAsync({
      catalog_asset_id: catalogAssetId,
      field_path: fieldPath,
      proposed_value: proposedValue,
      reason: reason || undefined,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-4 backdrop-blur-sm">
      <form onSubmit={submit} className="w-full max-w-xl overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-dark-border dark:bg-dark-surface">
        <div className="border-b border-gray-200 px-6 py-5 dark:border-dark-border">
          <div className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-gray-500">Propose catalog correction</div>
          <h2 className="mt-1 font-title text-2xl text-charcoal dark:text-gray-100">Edit definition</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Creates a steward-review request. Provider writeback remains governed by the correction queue.</p>
        </div>
        <div className="space-y-4 px-6 py-5">
          <Alert variant="info" title="Governed writeback">
            This change is submitted as a correction proposal. Direct metadata saves require the catalog policy backend.
          </Alert>
          <label className="block">
            <span className="text-sm font-medium text-charcoal dark:text-gray-100">Field</span>
            <Input className="mt-1" value={fieldPath} onChange={(event) => setFieldPath(event.target.value)} />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-charcoal dark:text-gray-100">Proposed value</span>
            <Textarea
              className="mt-1 min-h-[120px]"
              value={proposedValue}
              onChange={(event) => setProposedValue(event.target.value)}
              required
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-charcoal dark:text-gray-100">Reason</span>
            <Textarea className="mt-1" value={reason} onChange={(event) => setReason(event.target.value)} />
          </label>
          {createCorrection.isError && (
            <Alert variant="error" title="Correction request failed">
              Confirm the asset is linked to a catalog provider and try again.
            </Alert>
          )}
        </div>
        <div className="flex justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4 dark:border-dark-border dark:bg-dark-surface-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="brand" disabled={createCorrection.isPending || !proposedValue.trim()}>
            {createCorrection.isPending ? <Spinner className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />}
            Submit proposal
          </Button>
        </div>
      </form>
    </div>
  );
}

export default function CatalogAppletPage() {
  const { assetId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasAnyPermission } = usePermissions();
  const active = pageFromPath(location.pathname);
  const parsedAssetId = assetId ? Number(assetId) : undefined;
  const [correctionTarget, setCorrectionTarget] = useState<number | null>(null);
  const canViewCorrectionQueue = hasAnyPermission(['catalog:admin', 'platform:admin']);
  const canCreateCorrection = hasAnyPermission(['catalog:write', 'platform:admin']);
  const canManageWarehouseMetadata = hasAnyPermission(['connections:update', 'platform:admin']);
  const canRunCatalogEnrichment = canCreateCorrection;
  const canProfileSamples = hasAnyPermission(['connections:sync', 'platform:admin']);
  const shouldLoadOverview = active === 'overview' || active === 'providers' || active === 'settings';
  const shouldLoadAssetList = active === 'assets' && !parsedAssetId;
  const shouldLoadAssetDetail = active === 'assets' && Boolean(parsedAssetId);
  const shouldLoadProviders = active === 'providers';
  const shouldLoadGlossary = false;
  const shouldLoadCorrections = active === 'corrections' && canViewCorrectionQueue;
  const assetPageParams = useMemo(() => catalogAssetPageParams(searchParams), [searchParams]);

  const overview = useCatalogOverview(shouldLoadOverview);
  const assets = useCatalogWarehouseAssetPage(assetPageParams, shouldLoadAssetList);
  const detail = useCatalogWarehouseAssetDetail(parsedAssetId, shouldLoadAssetDetail);
  const providers = useCatalogProviders(shouldLoadProviders);
  const glossary = useCatalogGlossary(shouldLoadGlossary);
  const corrections = usePendingCatalogCorrections(shouldLoadCorrections);

  const providerData = providers.data || overview.data?.providers || [];
  const correctionData = canViewCorrectionQueue ? corrections.data || [] : [];
  const assetData = assets.data?.items || [];

  const updateAssetQuery = useCallback(
    (patch: Partial<CatalogWarehouseAssetPageParams>, options?: { resetOffset?: boolean }) => {
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        const setOrDelete = (key: string, value: string | number | boolean | undefined) => {
          if (value === undefined || value === '' || value === false) {
            next.delete(key);
          } else {
            next.set(key, String(value));
          }
        };
        const hasPatch = (key: keyof CatalogWarehouseAssetPageParams) =>
          Object.prototype.hasOwnProperty.call(patch, key);

        if (hasPatch('search')) setOrDelete('search', patch.search);
        if (hasPatch('warehouse_connector_id')) setOrDelete('warehouse_connector_id', patch.warehouse_connector_id);
        if (hasPatch('link_status')) setOrDelete('link_status', patch.link_status);
        if (hasPatch('has_pii')) setOrDelete('has_pii', patch.has_pii);
        if (hasPatch('limit')) setOrDelete('limit', patch.limit);
        if (hasPatch('offset')) setOrDelete('offset', patch.offset);
        if (options?.resetOffset) next.delete('offset');
        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  const refetchAll = () => {
    if (shouldLoadOverview) overview.refetch();
    if (shouldLoadAssetList) assets.refetch();
    if (shouldLoadProviders) providers.refetch();
    if (shouldLoadGlossary) glossary.refetch();
    if (shouldLoadCorrections) corrections.refetch();
    if (shouldLoadAssetDetail) detail.refetch();
  };

  const navigatePage = (page: CatalogPageId) => {
    if (page === 'overview') navigate('/catalog');
    if (page === 'assets') navigate('/catalog/assets');
    if (page === 'corrections' && canViewCorrectionQueue) navigate('/catalog/corrections');
    if (page === 'providers') navigate('/catalog/providers');
    if (page === 'settings') navigate('/catalog/settings');
  };

  const blockingError =
    (shouldLoadOverview ? overview.error : null) ||
    (shouldLoadAssetList ? assets.error : null) ||
    (shouldLoadAssetDetail ? detail.error : null) ||
    (shouldLoadProviders ? providers.error : null) ||
    (shouldLoadCorrections ? corrections.error : null);
  const isLoading =
    (shouldLoadOverview && overview.isLoading) ||
    (shouldLoadAssetList && assets.isLoading) ||
    (shouldLoadAssetDetail && detail.isLoading) ||
    (shouldLoadProviders && providers.isLoading) ||
    (shouldLoadCorrections && corrections.isLoading);

  const content = useMemo(() => {
    if (blockingError) return <ApiState error={blockingError} onRetry={refetchAll} />;
    if (isLoading) {
      return (
        <CatalogSurface className="flex min-h-[460px] items-center justify-center">
          <Spinner label="Loading catalog applet" />
        </CatalogSurface>
      );
    }
    if (active === 'overview') {
      return (
        <CatalogOverviewSurface
          overview={overview.data}
          assets={assetData}
          corrections={correctionData}
          providers={providerData}
          onNavigate={navigatePage}
          onSync={refetchAll}
          canViewCorrectionQueue={canViewCorrectionQueue}
        />
      );
    }
    if (active === 'assets' && parsedAssetId) {
      return (
        <CatalogAssetDetailSurface
          detail={detail.data}
          loading={detail.isLoading}
          onRequestCorrection={(catalogAssetId) => setCorrectionTarget(catalogAssetId)}
          onBack={() => navigate('/catalog/assets')}
          onRefetch={() => detail.refetch()}
          canCreateCorrection={canCreateCorrection}
          canManageWarehouseMetadata={canManageWarehouseMetadata}
          canRunCatalogEnrichment={canRunCatalogEnrichment}
          canProfileSamples={canProfileSamples}
        />
      );
    }
    if (active === 'assets') {
      return (
        <CatalogAssetsSurface
          assetPage={assets.data}
          query={assetPageParams}
          isFetching={assets.isFetching}
          onQueryChange={updateAssetQuery}
        />
      );
    }
    if (active === 'corrections' && !canViewCorrectionQueue) {
      return (
        <CatalogSurface>
          <EmptyState title="Correction queue unavailable" description="Catalog steward permissions are required to review correction proposals." />
        </CatalogSurface>
      );
    }
    if (active === 'corrections') return <CatalogCorrectionsSurface corrections={correctionData} />;
    if (active === 'providers') return <CatalogProvidersSurface overview={overview.data} providers={providerData} />;
    return <CatalogSettingsSurface overview={overview.data} />;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    active,
    assetData,
    assetPageParams,
    assets.data,
    assets.isFetching,
    blockingError,
    canCreateCorrection,
    canManageWarehouseMetadata,
    canProfileSamples,
    canRunCatalogEnrichment,
    canViewCorrectionQueue,
    correctionData,
    detail.data,
    detail.isLoading,
    isLoading,
    overview.data,
    parsedAssetId,
    providerData,
    updateAssetQuery,
  ]);

  return (
    <div className="flex h-full min-h-0 flex-col bg-gray-50 dark:bg-dark-bg">
      <div className="flex-1 overflow-y-auto px-8 py-7">
        <div className="mx-auto max-w-[1320px]">
          {content}
          <div className="h-10" />
        </div>
      </div>
      {correctionTarget !== null && canCreateCorrection && (
        <CorrectionDialog catalogAssetId={correctionTarget} onClose={() => setCorrectionTarget(null)} />
      )}
    </div>
  );
}
